<template>

    <v-row justify="center" no-gutters class="px-lg-15">
        <Alert :key="NaN" v-if="deleteSelectedAudioElementDialog" :dialogVal="true" type="dialog" title="Are you sure you want to delete this Audio Element?" text="Your Audio Element will be permanently deleted. This action cannot be undone." @dialogClicked="deleteAudioElement"/>
        <v-col cols="12" xl="7" lg="8" md="12" sm="12" xs="12" class="pr-lg-5">
            <v-row v-if="viewLoading || !letUserSeePost" justify="center" no-gutters>
                <v-col cols="12">
                    <v-skeleton-loader height="600" type="image" class="hidden-md-and-down"></v-skeleton-loader>
                    <!-- <v-skeleton-loader height="600" type="image" class="hidden-xl-only hidden-md-and-down"></v-skeleton-loader> -->
                    <v-skeleton-loader height="450" type="image" class="hidden-lg-and-up hidden-xs-only"></v-skeleton-loader>
                    <v-skeleton-loader height="200" type="image" class="hidden-sm-and-up"></v-skeleton-loader>
                </v-col>
            </v-row>

            <v-row v-else>
                <v-col cols="12">
                    <!-- @ready="playerReady=true" -->
                    <!-- @seeked="[currentAudioSegment, nextAudioSegment] = [null, null]" -->
                    <!-- @firstPlay="firstPlay" -->
                    <!-- @markerReached="markerReached" -->
                    <!-- @markerClicked="markerClicked" -->
                    <VideoPlayer
                        v-if="postData.videoUrl && postData.videoType"
                        ref="vidPlayer"
                        :options="vidOptions"
                        :isCreating="false"
                        :videoLoading="videoLoading"
                        :anonymousMode="postData.anonymousMode || false"
                        :playbackStyle="playbackStyle"
                        :playbackStyleAudioFirst="playbackStyleAudioFirst"
                        :playbackStyleSameStart="playbackStyleSameStart" 
                        :showCaptions="showCaptions"
                        @seeked="videoPlayerSeeked"
                        @hasBegunPlaying="onHasBegunPlaying"
                        @playAudioPlayer="playAudioPlayer"
                        @pauseAudioPlayer="pauseAudioPlayer"
                        @controlsEnabled="enableControls"
                        @playerReady="getAudioData"
                        @timeUpdate="timeUpdate"
                        @advancedControlsClicked="viewAdvancedControls = !viewAdvancedControls"
                    />
                </v-col>
            </v-row>

            <!-- Settings status bar — conditional, read-only. Appears when session settings deviate
                 from the user's baseline (stored settings, falling through to CANONICAL_DEFAULTS),
                 or when the post was opened via a share link with &share=1 (absorbs the old sender
                 settings banner). Icons reuse the Advanced Controls state glyphs. Tapping an icon
                 opens Advanced Controls on that setting — it never changes a setting directly.
                 Dismissal is session-only; the bar returns if the set of anomalies changes. -->
            <v-row v-if="showSettingsStatusBar" justify="center" no-gutters class="mt-1 mb-0">
                <v-col cols="12">
                    <v-alert
                        type="info"
                        dense
                        class="mb-0 text-body-2"
                        dismissible
                        @input="dismissStatusBar"
                    >
                        <div v-if="senderSettingsActive || settingsAnomalies.length">
                            <span v-if="senderSettingsActive">Viewing with sender's settings · </span>
                            <span v-else>Changed from your usual settings · </span>
                            <a href="#" class="font-weight-medium white--text" @click.prevent="resetToMySettings">{{ senderSettingsActive ? 'Use my defaults' : 'Reset' }}</a>
                            <span v-if="settingsAnomalies.length" class="ml-2" style="white-space: nowrap;">
                                <v-btn
                                    v-for="anomaly in settingsAnomalies"
                                    :key="anomaly.key"
                                    icon
                                    small
                                    dark
                                    :ripple="false"
                                    :title="anomaly.label"
                                    :aria-label="anomaly.label"
                                    @click.stop="openAdvancedControlFromStatusBar(anomaly)"
                                >
                                    <component v-if="anomaly.component" :is="anomaly.component" :size="18" style="color: white"/>
                                    <v-icon v-else small color="white">{{ anomaly.icon }}</v-icon>
                                </v-btn>
                            </span>
                        </div>
                        <!-- Overlap hint, folded in: educational once-per-session line, not an anomaly -->
                        <div v-if="showOverlapHint" :class="{ 'mt-1': senderSettingsActive || settingsAnomalies.length }">
                            🎙 Caption audio is playing — lower video volume to hear it clearly · 
                            <a href="#" class="font-weight-medium white--text" @click.prevent="muteVideo">Mute video</a>
                        </div>
                        <!-- Dead-air hint, same pattern: pauseplay with muted caption audio and hidden
                             subtitles means every pause is pure silence — explain it and offer the fix -->
                        <div v-if="showDeadAirHint" :class="{ 'mt-1': senderSettingsActive || settingsAnomalies.length || showOverlapHint }">
                            🔇 Caption audio is muted — playback will pause in silence · 
                            <a href="#" class="font-weight-medium white--text" @click.prevent="unmuteCaptionAudio">Unmute</a>
                        </div>
                    </v-alert>
                </v-col>
            </v-row>

            <!-- Show More/Less Button to view description, playback style, and caption audio -->
            <div v-if="letUserSeePost" style="position: relative; max-width: 100%;" class="full-width">
                <v-row justify="space-around" align="center" no-gutters class="mt-5">
                    <v-col cols="10" :class="viewFullDescription ? '' : 'd-inline-block text-truncate'">
                        <span v-if="!editPost" class="text-h5 text-xl-h4 text-lg-h4 font-weight-bold">{{postData.title}}</span>
                        <v-text-field v-else ref="editTitle" v-model="postData.title" @change="postMetadataHasChanged = true" :rules="titleRules" :counter="50" outlined class="text-subtitle-1 text-xl-h5 text-lg-h5 font-weight-bold"></v-text-field>
                    </v-col>

                    <v-col cols="2" align="end">
                        <v-menu v-model="menu" rounded="lg" offset-y bottom origin="center" transition="scale-transition" close-on-content-click>
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" @click.stop="menu = !menu" icon :disabled="editPost">
                                    <v-icon :color="$vuetify.theme.dark ? 'amber' : 'primary'">mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>
                            <v-list rounded>
                                <v-list-item @click.stop="showShareDialog = true, menu = !menu">
                                    <v-icon class="pr-2">mdi-share</v-icon>Share<br>
                                </v-list-item>
                                <v-list-item @click.stop="reportDialog = true, menu = !menu">
                                    <v-icon class="pr-2">mdi-flag</v-icon>Report
                                </v-list-item>
                                <v-list-item v-if="isUsersPost() && postData.thumbnail != '' && postData.thumbnail != null" @click.stop="triggerFileUpload(), menu = !menu"> 
                                    <v-icon class="pr-2">mdi-image-plus</v-icon>Change Thumbnail<br>
                                </v-list-item>
                                <v-list-item v-if="isUsersPost() && (postData.thumbnail === '' || postData.thumbnail === null)" @click.stop="triggerFileUpload(), menu = !menu"> 
                                    <v-icon class="pr-2">mdi-image-plus</v-icon>Add Post Thumbnail<br>
                                </v-list-item>
                                <v-list-item v-if="isUsersPost()" @click.stop="editPost = true, viewFullDescription = true, menu = !menu"> 
                                    <v-icon class="pr-2">mdi-pencil</v-icon>Edit Title & Description<br>
                                </v-list-item>
                                <v-list-item v-if="isUsersPost()" @click.stop="$store.dispatch('downloadVttFile', {post: postData, audio: audioData}), menu = !menu"><v-icon class="pr-2">mdi-file-download</v-icon>Download VTT</v-list-item>
                                <v-list-item v-if="isUsersPost()" @click.stop="updateAnonymousMode(), menu = !menu">
                                    <v-icon class="pr-2">{{postData.anonymousMode ? 'mdi-incognito-circle' : 'mdi-incognito-circle-off'}}</v-icon>Anonymous Mode: {{postData.anonymousMode ? "ON" : "OFF"}}<br>
                                </v-list-item>
                                <!-- <v-list-item @click.stop="viewAdvancedControls = true, menu = !menu">
                                    <v-icon class="pr-2">mdi-tune</v-icon>Advanced Controls<br>
                                </v-list-item> -->
                            </v-list>
                        </v-menu>
                        <Report v-if="reportDialog" :dialog="reportDialog" page="viewPost" :post="postData" @close-report="reportDialog = false"/>
                        <UploadPhotoDialog v-if="uploadPhotoDialog" :userId="userData.userId" :dialog="uploadPhotoDialog" :postType="postData.postType" :postId="postData.postId" :existingThumbnail="(postData.thumbnail != '' && postData.thumbnail != null) " @close-dialog="uploadPhotoDialog = false" @thumbnail-upload-success="thumbnailUploadSuccess"/>
                        <ShareDialog
                            v-if="showShareDialog"
                            :dialog="showShareDialog"
                            :postData="postData"
                            :senderSettings="{
                                ps: playbackStyle,
                                sc: showCaptions ? '1' : '0',
                                cv: String(captionVolume),
                                vv: String(videoVolume)
                            }"
                            @close-dialog="showShareDialog = false"
                        />
                    </v-col>
                </v-row>
                <div v-if="viewFullDescription" style="position: relative; max-width: 100%;" class="full-width">
                    <v-row v-if="letUserSeePost" justify="start" align="center" no-gutters class="my-3">
                        <router-link v-if="postData.userId === currentUserId" :to="{ path: 'profile' }" class="text-decoration-none accent--text">
                            <p class="subtitle-2 font-weight-light font-italic mb-0 py-0 accent--text"> @{{postData.username ? postData.username : 'Unknown' }}</p>
                        </router-link>
                        <router-link v-else :to="{name: 'view-user-profile', query: {id: postData.userId}}" class="text-decoration-none accent-text">
                            <p class="subtitle-2 font-weight-light font-italic mb-0 py-0 accent--text"> @{{postData.username ? postData.username : 'Unknown' }}</p>
                        </router-link>
                        <span class="px-2">|</span>
                        <p class="subtitle-2 font-weight-light mb-0 py-0">{{postData.postDate}}</p>
                    </v-row>

                    <v-row v-if="letUserSeePost" justify="start" align="center" no-gutters>
                        <span v-if="!editPost" style="max-width: 100%; overflow-x: hidden;" class="subtitle-1 text--secondary font-weight-light">{{postData.description}}</span>
                        <v-textarea v-else ref="editDescription" v-model="postData.description" outlined style="max-width: 100%; overflow-x: hidden;" class="subtitle-1 font-weight-light" :counter="750" :rules="descriptionRules" @update:error="postMetadataError = true" @change="postMetadataHasChanged = true"></v-textarea>
                    </v-row>


                    <!-- OLD ADVANCED CONTROLS -- WILL NOW TELL USER HOW TO OPEN ADVANCED CONTROLS FROM VIDEO PLAYER -->
                    <v-row v-if="letUserSeePost && !editPost" justify="space-between" align="center" no-gutters class="my-2">
                        <v-col cols="12" xl="4" lg="5" md="5" sm="5" class="my-md-0 my-1">
                            <span class="mr-2 text-subtitle-2 font-weight-light">*Advanced Controls moved to Video Player <span class="font-weight-bold font-italic">Playback Bar</span> above. You can open them by clicking the "<v-icon>mdi-tune</v-icon>" icon!*</span>
                        </v-col>
                        <!-- ****** COMMENTED OUT BY SCOTT BRADY ON DECEMBER 12, 2025 ****** -->
                        <!-- <v-col cols="12" xl="4" lg="5" md="5" sm="5" class="my-md-0 my-1">
                            <v-radio-group v-model="playbackStyle" label="Playback:" row dense hide-details class="my-0">
                                <v-tooltip top>
                                    <template v-slot:activator="{ on }">
                                        <v-radio class="hidden-sm-and-down" v-on="on" label="Continuous" value="continuous"></v-radio>
                                        <v-radio class="hidden-md-and-up" label="Continuous" value="continuous"></v-radio>
                                    </template>
                                    <span>Play the captions and audio without the video pausing</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on }">
                                        <v-radio class="hidden-sm-and-down" v-on="on" label="Pause/Play" value="pauseplay"></v-radio>
                                        <v-radio class="hidden-md-and-up" label="Pause/Play" value="pauseplay"></v-radio>
                                    </template>
                                    <span>The video will pause while the captions and audio play</span>
                                </v-tooltip>
                            </v-radio-group>
                        </v-col>

                        <v-col cols="12" xl="3" lg="3" md="2" sm="2" class="my-md-0 my-1">
                            <v-row :justify="isMobile ? 'start' : 'center'" align="center" class="ma-0 pa-0">
                                <span class="mr-2 text-subtitle-2 font-weight-light">Captions:</span>
                                <v-switch v-model="showCaptions" color="primary" :label="showCaptions ? 'ON' : 'OFF'"></v-switch>
                            </v-row>
                        </v-col>

                        <v-col cols="12" xl="5" lg="4" md="5" sm="5" class="my-md-0 my-1">
                            <v-slider
                                v-model="captionVolume"
                                label="Caption Audio"
                                inverse-label
                                :prepend-icon="captionVolume > 0 ? 'mdi-volume-medium' : 'mdi-volume-variant-off'"
                                @click:prepend="toggleCaptionAudioMute"
                                @click:append="adjustSegmentVolume(1.0)"
                                min="0"
                                max="1"
                                step="0.1"
                                v-on:end="adjustSegmentVolume"
                                v-on:click="adjustSegmentVolume(captionVolume)"
                                dense
                                hide-details
                            ></v-slider>
                        </v-col> -->
                    </v-row>
                    <!-- END OLD ADVANCED CONTROLS -->
                </div>
                <div v-if="!viewFullDescription && !editPost" v-bind:class="[$vuetify.theme.dark ? 'faded-dark' : 'faded']"></div>
                <div v-if="!editPost" style="width: 100%;" class="d-flex justify-space-around align-center">
                    <v-divider/>
                    <v-btn text x-small :ripple="false" color="primary" @click="viewFullDescription = !viewFullDescription">
                        <v-icon color="primary">{{ viewFullDescription ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
                        {{ viewFullDescription ? 'View Less' : 'View More'}}
                        <v-icon color="primary">{{ viewFullDescription ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
                    </v-btn>
                    <v-divider/>
                </div>
                <v-row v-if="editPost && letUserSeePost" justify="center" no-gutters>
                    <v-btn text color="primary" :disabled="editPostMetadataLoading" :loading="editPostMetadataLoading" @click.stop="editPostMetadata">save</v-btn>
                    <v-btn text color="red" :disabled="editPostMetadataLoading" @click.stop="editPost = false">cancel</v-btn>
                </v-row>
            </div>
        </v-col>

        <!-- START - VIEW ADVANCED CONTROLS -->
        <v-col v-if="viewAdvancedControls" cols="12" xl="3" lg="4" md="12" sm="12" :style="{maxHeight: getMaxHeightMainContainer}" style="max-height: 100vh; overflow-y: hidden; overflow-x: hidden;" class="bordered mt-5 mt-xl-0 mt-lg-0 d-flex flex-column">
            <!-- Advanced Controls Header -->
            <v-row justify="space-between" align="center" no-gutters class="mx-0 py-2 py-lg-0 flex-grow-1">
                <span style="font-size: 1.5em" class="ml-2"><v-icon class="mx-2">mdi-tune</v-icon>Advanced Controls</span>
                <v-btn icon :ripple="false" class="mr-2" @click="viewAdvancedControls = false, showLanguageSelectionMenu = false"><v-icon size="2em">mdi-close</v-icon></v-btn>
            </v-row>
            <v-divider/>
            <!-- END Advanced Controls Header -->

            <!-- Advanced Controls Title/Subtitle -->
            <v-row justify="center" align="center" no-gutters class="mt-0 px-5 py-2 py-md-0 mt-0 flex-grow-1">
                <v-col cols="12" align="center" class="">
                    <span style="font-size: 25px">{{ currentAudioElement != null ? currentAudioElement.audioData.title : 'Title' }}</span><br/>
                    <v-menu v-if="currentAudioElement != null && currentAudioElement?.audioData?.subtitlesByLanguage" v-model="showLanguageSelectionMenu" rounded="lg" offset-y bottom origin="center" transition="scale-transition" close-on-content-click>
                        <template v-slot:activator="{ on }">
                            <!-- <v-row justify="center" dense class="ma-1 red-border"> -->
                                <div v-on="on" style="cursor: pointer;" class="font-weight-light text-truncate language" @click="selectedSubtitleLanguage == null && (selectedSubtitleLanguage = currentAudioElement?.audioData?.languageName);">{{(`${((subtitlesByLanguageSelected) ? (selectedSubtitleLanguage.split("-")[1].trim()) : (currentAudioElement != null ? currentAudioElement.audioData.languageName : 'Language'))} + ${(currentAudioElement != null) ? Object.keys(currentAudioElement?.audioData.subtitlesByLanguage).length : '0'}`)}}</div>
                            <!-- </v-row> -->
                        </template>
                        <v-list rounded style="max-height: 315px; overflow-y: scroll;">
                            <v-list-item v-for="(language) in vMenuLanguages" :key="language" @click.stop="setSelectedSubtitleLanguage(language)">{{ language.includes('-') ? language.split("-")[1].trim() : language}}</v-list-item>
                        </v-list>
                    </v-menu>
                    <span style="font-size: 15px" class="grey--text">@{{ currentAudioElement != null ? currentAudioElement.audioData.username : 'username' }}</span>
                </v-col>
            </v-row>
            <!-- END Advanced Controls Title/Subtitle -->

            <!-- Advanced Controls Buttons -->
            <v-row justify="center" align="center" no-gutters class="ma-0 px-5 py-5 flex-grow-1">
                <v-col cols="12" align="center" class="hidden-md-and-down">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon x-large @click.stop="advancedControlsClick('previous')"><v-icon size="3rem">mdi-skip-previous</v-icon></v-btn>
                        </template>
                        <span>Play video from beginning</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon x-large @click.stop="advancedControlsClick('backward')"><v-icon size="2.5rem">mdi-skip-backward</v-icon></v-btn>
                        </template>
                        <span>Skip back to previous audio segments</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon large @click.stop="advancedControlsClick('replay')"><v-icon size="2rem">mdi-replay</v-icon></v-btn>
                        </template>
                        <span>Replay audio segment</span>
                    </v-tooltip>
                    <!-- <v-btn icon large @click.stop="advancedControlsClick('rewind')"><v-icon>mdi-undo</v-icon></v-btn> -->
                    <!-- <v-btn icon large @click.stop="advancedControlsClick('rewind')"><v-icon>mdi-rewind-10</v-icon></v-btn> -->
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon x-large :ripple="false" @click.stop="advancedControlsClick(videoIsPlaying ? 'pause' : 'play')"><v-icon size="4.5rem">{{videoIsPlaying ? 'mdi-pause' : 'mdi-play'}}</v-icon></v-btn>
                        </template>
                        <span>{{videoIsPlaying ? 'Pause' : 'Play'}} video</span>
                    </v-tooltip>
                    <!-- <v-btn icon large @click.stop="advancedControlsClick('fast')"><v-icon>mdi-fast-forward-10</v-icon></v-btn> -->
                    <!-- <v-btn icon large @click.stop="advancedControlsClick('fast')"><v-icon>mdi-redo</v-icon></v-btn> -->
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon large style="transform: scaleX(-1)" @click.stop="advancedControlsClick('skip')"><v-icon size="2rem">mdi-replay</v-icon></v-btn>
                        </template>
                        <span>Skip to next audio segment</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon x-large @click.stop="advancedControlsClick('forward')"><v-icon size="2.5rem">mdi-skip-forward</v-icon></v-btn>
                        </template>
                        <span>Skip to next audio segment</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" icon x-large @click.stop="advancedControlsClick('next')"><v-icon size="3rem">mdi-skip-next</v-icon></v-btn>
                        </template>
                        <span>Jump to end of audio segments</span>
                    </v-tooltip>
                </v-col>

                <v-col cols="12" align="center" class="hidden-lg-and-up">
                    <v-btn icon large @click.stop="advancedControlsClick('previous')"><v-icon size="2rem">mdi-skip-previous</v-icon></v-btn>
                    <v-btn icon x-large @click.stop="advancedControlsClick('backward')"><v-icon size="2rem">mdi-skip-backward</v-icon></v-btn>
                    <v-btn icon medium @click.stop="advancedControlsClick('replay')"><v-icon size="2rem">mdi-replay</v-icon></v-btn>
                    <!-- <v-btn icon large @click.stop="advancedControlsClick('rewind')"><v-icon>mdi-undo</v-icon></v-btn> -->
                    <!-- <v-btn icon medium @click.stop="advancedControlsClick('rewind')"><v-icon>mdi-rewind-10</v-icon></v-btn> -->
                    <v-btn icon x-large :ripple="false" @click.stop="advancedControlsClick(videoIsPlaying ? 'pause' : 'play')"><v-icon size="4rem">{{videoIsPlaying ? 'mdi-pause' : 'mdi-play'}}</v-icon></v-btn>
                    <!-- <v-btn icon medium @click.stop="advancedControlsClick('fast')"><v-icon>mdi-fast-forward-10</v-icon></v-btn> -->
                    <!-- <v-btn icon large @click.stop="advancedControlsClick('fast')"><v-icon>mdi-redo</v-icon></v-btn> -->
                    <v-btn icon medium style="transform: scaleX(-1)" @click.stop="advancedControlsClick('skip')"><v-icon size="2rem">mdi-replay</v-icon></v-btn>
                    <v-btn icon x-large @click.stop="advancedControlsClick('forward')"><v-icon size="2rem">mdi-skip-forward</v-icon></v-btn>
                    <v-btn icon large @click.stop="advancedControlsClick('next')"><v-icon size="2rem">mdi-skip-next</v-icon></v-btn>
                </v-col>
            </v-row>
            <!-- END Advanced Controls Buttons -->

            <!-- Advanced Controls Options -->
            <v-row v-if="!advancedControlsOptionsClicked" justify="center" align="center" no-gutters class="ma-0 px-5 py-5 flex-grow-1">
                <v-col cols="2" align="center" class="">
                    <v-btn icon large :ripple="false" @click.stop="advancedControlsClick('captions')"><v-icon>{{captionsIcon}}</v-icon></v-btn><br/>
                    <span style="font-size: 0.70em" class="ma-0 pa-0">captions</span>
                </v-col>
                <v-col cols="2" align="center" class="">
                    <v-btn icon large :ripple="false" @click.stop="advancedControlsClick('playbackStyle')"><v-icon>{{playbackStyleIcon}}</v-icon></v-btn><br/>
                    <span style="font-size: 0.70em" class="ma-0 pa-0">style</span>
                </v-col>
                <v-col cols="2" align="center" class="">
                    <v-btn icon large :ripple="false" @click.stop="advancedControlsClick('volume')">
                        <v-icon>{{captionVolumeIcon}}</v-icon>
                    </v-btn><br/>
                    <span style="font-size: 0.70em" class="ma-0 pa-0">volume</span>
                </v-col>
                <v-col cols="2" align="center" class="">
                    <v-btn icon large :ripple="false" @click.stop="advancedControlsClick('speed')"><v-icon>{{speedIcon}}</v-icon></v-btn><br/>
                    <span style="font-size: 0.70em" class="ma-0 pa-0">speed</span>
                </v-col>
                <!-- <v-col cols="2" align="center" class="pa-4">
                    <v-menu v-model="showLanguageSelectionMenu" rounded="lg" offset-y bottom origin="center" transition="scale-transition" close-on-content-click>
                        <template v-slot:activator="{ on }">
                            <v-row justify="center" class="mx-0 ml-0 pb-3">
                                <v-btn v-on="on" icon large :ripple="false" @click.stop="advancedControlsClick('languages')"><v-icon>mdi-translate-variant</v-icon></v-btn><br/>
                            </v-row>
                            <v-row justify="center" class="mx-0 ml-0 pt-1">
                                <span v-on="on" style="font-size: 0.70em" class="ma-0 pa-0">languages</span>
                            </v-row>
                        </template>
                        <v-list rounded style="max-height: 315px; overflow-y: scroll;">
                            <v-list-item v-for="(language) in vMenuLanguages" :key="language" @click.stop="setSelectedSubtitleLanguage(language)">{{ language.includes('-') ? language.split("-")[1].trim() : language}}</v-list-item>
                        </v-list>
                    </v-menu>
                </v-col> -->
            </v-row>

            <!-- Advanced Controls Clicked to Show Selected Option -->
            <v-row v-else justify="center" align="center" no-gutters style="position: relative;" class="outlined ma-0 px-5 py-5 flex-grow-1">
                <v-btn icon :ripple="false" style="position: absolute; top: 0; right: 0;" @click="closeAdvancedOptionsClicked"><v-icon size="2em">mdi-close</v-icon></v-btn>
                <v-col v-if="advancedControl == 'volume'" cols="12" align="center" class="">
                    <span class="text-decoration-underline">Caption Audio Volume</span><br/>
                    <!-- :prepend-icon="captionVolume > 0 ? 'mdi-volume-medium' : 'mdi-volume-variant-off'" -->
                    <!-- v-on:click="adjustSegmentVolume(captionVolume)" -->
                    <v-slider
                        v-model="captionVolume"
                        v-on:end="adjustSegmentVolume"
                        dense
                        hide-details
                        min="0"
                        max="1"
                        step="0.1"
                        :ripple="false"
                        :prepend-icon="captionVolumeIcon"
                        class="px-2"
                        @click:prepend="toggleCaptionAudioMute"
                        @click:append="adjustSegmentVolume(1.0)"
                    ></v-slider>
                </v-col>

                <v-col v-else-if="advancedControl == 'playbackStyle'" cols="12" align="center" class="">
                    <span class="text-decoration-underline">Caption Playback Style</span><br/>
                    <v-row justify="space-around" no-gutters class="mt-1">
                        <v-btn text :ripple="false" class="px-3" :style="{ opacity: playbackStyle === 'continuous' ? 1 : 0.55, textTransform: 'none' }" title="Play the captions and audio without the video pausing" @click.stop="setPlaybackStyle('continuous')">
                            <v-icon :color="playbackStyle === 'continuous' ? 'primary' : 'grey'">mdi-autorenew</v-icon>
                            <span class="ml-1 text-caption" :class="playbackStyle === 'continuous' ? 'white--text' : 'grey--text'">Continuous</span>
                        </v-btn>
                        <v-btn text :ripple="false" class="px-3" :style="{ opacity: playbackStyle === 'pauseplay' ? 1 : 0.55, textTransform: 'none' }" title="The video will pause while the captions and audio play" @click.stop="setPlaybackStyle('pauseplay')">
                            <v-icon :color="playbackStyle === 'pauseplay' ? 'primary' : 'grey'">mdi-play-pause</v-icon>
                            <span class="ml-1 text-caption" :class="playbackStyle === 'pauseplay' ? 'white--text' : 'grey--text'">Pause/Play</span>
                        </v-btn>
                    </v-row>
                    <!-- Overlap mode — only meaningful in continuous playback. Both custom glyphs read
                         as the same left-to-right sentence (current segment's fate, then the overlapping
                         segment's fate): skip = plays, then prohibited; chop = chopped off (mdi scissors path),
                         then the next plays. Native title attributes for hover text; short labels stay for
                         readers. Session-only, like every other Advanced Control. -->
                    <template v-if="playbackStyle === 'continuous'">
                        <span class="text-decoration-underline d-inline-block mt-2">Overlapping Segments</span><br/>
                        <v-row justify="space-around" no-gutters class="mt-1">
                            <v-btn text :ripple="false" class="px-3" :style="{ opacity: playbackStyleSameStart ? 1 : 0.55 }" title="Skip overlapping segments" @click.stop="setOverlapMode(true)">
                                <span :style="{ color: playbackStyleSameStart ? 'var(--v-primary-base)' : '#9e9e9e' }"><icon-overlap-skip/></span><span class="ml-1 text-caption" :class="playbackStyleSameStart ? 'white--text' : 'grey--text'">skip</span>
                            </v-btn>
                            <v-btn text :ripple="false" class="px-3" :style="{ opacity: !playbackStyleSameStart ? 1 : 0.55 }" title="Chop the current segment off early to play the next" @click.stop="setOverlapMode(false)">
                                <span :style="{ color: !playbackStyleSameStart ? 'var(--v-primary-base)' : '#9e9e9e' }"><icon-overlap-chop/></span><span class="ml-1 text-caption" :class="!playbackStyleSameStart ? 'white--text' : 'grey--text'">chop</span>
                            </v-btn>
                        </v-row>
                    </template>
                </v-col>

                <v-col v-else-if="advancedControl == 'speed'" cols="12" align="center" class="">
                    <span class="text-decoration-underline">Playback Speed</span><br/>
                    <v-radio-group v-model="playbackRate" row dense hide-details class="ma-0">
                        <v-row justify="space-around" no-gutters>
                            <v-radio label="0.5x" :value="0.50"></v-radio>
                            <v-radio label="0.75x" :value="0.75"></v-radio>
                            <v-radio label="1.0x" :value="1.0"></v-radio>
                            <v-radio label="1.5x" :value="1.5"></v-radio>
                            <v-radio label="2.0x" :value="2.0"></v-radio>
                        </v-row>
                    </v-radio-group>
                </v-col>
            </v-row>
            <!-- END Advanced Controls Clicked to Show Selected Option -->

            <v-row justify="center" align="center" no-gutters style="overflow-x: hidden; overflow-y: scroll" class="mt-0 px-5 py-2 flex-grow-2">
                <v-col v-if="showCaptions" cols="12" align="center" class="">
                    <span v-if="previousAudioSegment !== null" style="font-size: 10px" class="grey--text">{{ previousAudioSegment?.subtitle?.length > 36 ? (previousAudioSegment?.index + 1) + ". " + previousAudioSegment?.subtitle.substring(0, 36) + '...' : (previousAudioSegment?.index + 1) + ". " + previousAudioSegment?.subtitle }}</span><br/>
                    <!-- <span v-if="currentAudioSegment !== null" :style="{ fontSize: (currentAudioSegment?.subtitle?.length >= 50) ? '10px;' : '25px;'}" :class="!displayCurrentSubtitle ? 'grey--text' : ''" class="text-break">{{ currentAudioSegment?.segment + ". " +  "Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, " }}</span><br/> -->
                    <!-- <span v-if="currentAudioSegment !== null" style="font-size: 25px" :class="!displayCurrentSubtitle ? 'grey--text' : ''" class="text-break">{{ currentAudioSegment?.segment + ". " + "If you tell me how you initialize Video.js (the videojs(...) options object), I can point you to the exact spot to drop subsCapsButton: false so it’s clean and consistent across your app." }}</span><br/> -->
                    <span v-if="currentAudioSegment !== null" style="font-size: 25px" :class="!displayCurrentSubtitle ? 'grey--text' : ''" class="text-break">{{ (currentAudioSegment?.index + 1) + ". " +  currentAudioSegment?.subtitle }}</span><br/>
                    <span v-if="nextAudioSegment !== null" style="font-size: 10px" class="grey--text">{{ nextAudioSegment?.subtitle?.length > 36 ? (nextAudioSegment?.index + 1) + ". " + nextAudioSegment?.subtitle.substring(0, 36) + '...' : (nextAudioSegment?.index + 1) + ". " + nextAudioSegment?.subtitle }}</span>
                </v-col>
                <v-col v-else cols="12" align="center" class="">
                    <span style="font-size: 25px" class="grey--text">Subtitles will show here when turned on</span>
                </v-col>
            </v-row>
            <!-- END Advanced Controls Options -->
        </v-col>
        <!-- END - VIEW ADVANCED CONTROLS -->

        <v-col v-else cols="12" xl="3" lg="4" md="12" sm="12" :style="{maxHeight: getMaxHeightMainContainer}" style="max-height: 100vh; overflow-y: hidden; overflow-x: hidden;" class="bordered mt-5 mt-xl-0 mt-lg-0">

            <!-- Search + language filter combined row. Same placement on mobile and desktop. -->
            <v-row justify="center" align="center" no-gutters class="mx-0 mt-2 px-5">
                <v-col cols="12" xl="10" lg="11" md="12" sm="12">
                    <v-row no-gutters align="center" style="gap: 8px; flex-wrap: nowrap;">
                        <v-col style="flex: 1 1 0; min-width: 140px;">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <div v-bind="attrs" v-on="on">
                                        <v-text-field
                                            v-model="searchQuery"
                                            :label="isMobile ? null : 'Search captions'"
                                            :placeholder="isMobile ? 'Search captions' : null"
                                            prepend-inner-icon="mdi-magnify"
                                            clearable
                                            hide-details
                                            dense
                                            single-line
                                        ></v-text-field>
                                    </div>
                                </template>
                                <span>Search all subtitles on this video</span>
                            </v-tooltip>
                        </v-col>
                        <div v-if="filter.languageCodes.length > 1 && !audioLoading" style="flex: 0 0 auto;">
                            <!-- Filter control: button when inactive, chip-style button when active. Menu opens on click. -->
                            <v-menu v-model="filterMenu" rounded="lg" offset-y bottom origin="center" transition="scale-transition" :close-on-content-click="true" max-height="400">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn
                                        v-if="filter.languageSelected === 'All'"
                                        v-on="on"
                                        v-bind="attrs"
                                        icon
                                        outlined
                                        color="primary"
                                        :title="'Filter by audio language'"
                                        :aria-label="'Filter by audio language'"
                                        style="height: 36px; width: 36px;"
                                    >
                                        <v-icon>mdi-filter-variant</v-icon>
                                    </v-btn>
                                    <v-btn
                                        v-else
                                        v-on="on"
                                        v-bind="attrs"
                                        outlined
                                        small
                                        color="primary"
                                        :title="'Filtering by ' + filter.languageSelected + ' — tap to change, or ✕ to clear'"
                                        style="height: 36px; text-transform: none; font-weight: 500;"
                                    >
                                        <v-icon left small>mdi-filter</v-icon>
                                        {{ compactLanguageLabel(filter.languageSelected) }}
                                        <v-icon right small @click.stop.prevent="clearLanguageFilter">mdi-close-circle</v-icon>
                                    </v-btn>
                                </template>
                                <v-list rounded dense>
                                    <v-list-item
                                        v-for="code in filter.languageCodes"
                                        :key="code"
                                        @click="filter.languageSelected = code"
                                        :class="filter.languageSelected === code ? 'v-list-item--active primary--text' : ''"
                                    >
                                        <v-list-item-title>{{ code === 'All' ? 'All languages' : code }}</v-list-item-title>
                                    </v-list-item>
                                </v-list>
                            </v-menu>
                        </div>
                        <!-- Color picker toggle: small dot showing current color. Tap/click to expand swatches. -->
                        <div v-if="searchQuery" style="flex: 0 0 auto;">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <div
                                        v-bind="attrs"
                                        v-on="on"
                                        @click.stop="showColorPicker = !showColorPicker"
                                        :style="{
                                            backgroundColor: searchHighlightColor,
                                            width: '22px',
                                            height: '22px',
                                            borderRadius: '50%',
                                            cursor: 'pointer',
                                            border: showColorPicker ? '2px solid white' : '2px solid rgba(255,255,255,0.4)',
                                            boxShadow: showColorPicker ? '0 0 0 1px ' + searchHighlightColor : 'none',
                                            transition: 'border 0.15s, box-shadow 0.15s',
                                        }"
                                    ></div>
                                </template>
                                <span>{{ showColorPicker ? 'Hide color options' : 'Change search highlight color' }}</span>
                            </v-tooltip>
                        </div>
                    </v-row>
                </v-col>
            </v-row>
            <!-- Search highlight color picker — expands when user taps the color dot -->
            <v-expand-transition>
                <v-row v-if="searchQuery && showColorPicker" justify="center" align="center" no-gutters class="mx-0 mt-2 px-5">
                    <v-col cols="12" xl="10" lg="11" md="12" sm="12">
                        <div class="d-flex align-center justify-start" style="gap: 8px;">
                            <span class="text-caption grey--text">Highlight:</span>
                            <div
                                v-for="color in searchColorOptions"
                                :key="color"
                                @click.stop="setSearchColor(color); showColorPicker = false;"
                                :style="{
                                    backgroundColor: color,
                                    width: '22px',
                                    height: '22px',
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    border: searchHighlightColor === color ? '2px solid white' : '2px solid transparent',
                                    boxShadow: searchHighlightColor === color ? '0 0 0 1px ' + color : 'none',
                                }"
                            ></div>
                        </div>
                    </v-col>
                </v-row>
            </v-expand-transition>
            <v-row justify="center" align="center" class="mx-0 mb-2 mt-2 px-5">
                <v-divider/>
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" @click.stop="addNewAudio()" depressed color="primary" :disabled="audioLoading" class="hidden-sm-and-down">Contribute
                            <v-icon right>mdi-plus</v-icon>
                            <!-- <v-icon right>mdi-microphone-plus</v-icon> -->
                        </v-btn>
                        <v-btn @click.stop="addNewAudio()" depressed color="primary" :disabled="audioLoading" style="max-width: 60%;" class="hidden-md-and-up">Contribute
                            <v-icon right>mdi-plus</v-icon>
                            <!-- <v-icon right>mdi-microphone-plus</v-icon> -->
                        </v-btn>
                    </template>
                    <span>Add your own audio</span>
                </v-tooltip>
                <v-divider/>
            </v-row>

            <v-row :style="{maxHeight: getMaxHeightSubContainer}" style="max-height: 100vh; overflow-y: scroll;" class="bordered mx-0 px-5">
                <v-row v-if="audioLoading" class="my-0 py-0 hidden-lg-and-up hidden-xs-only">
                    <v-col v-for="i in 3" :key="i" cols="6" class="my-0 pt-0 px-2 pb-5 hidden-lg-and-up hidden-xs-only">
                        <v-skeleton-loader height="100" type="image" class="rounded-lg"></v-skeleton-loader>
                    </v-col>
                    <v-col v-for="i in 3" :key="i+3" cols="6" class="my-0 pt-0 px-2 pb-5 hidden-lg-and-up hidden-xs-only">
                        <v-skeleton-loader height="100" type="image" class="rounded-lg"></v-skeleton-loader>
                    </v-col>
                </v-row>

                <v-col v-if="leftAudioData.length > 0" cols="12" md="6" lg="12" xl="12" class="hidden-xs-only hidden-lg-and-up ma-0 pl-0 py-0 justify-end">
                    <v-container fluid v-for="audio in leftAudioData" :key="audio.audioDataId" class="pt-0 px-0 pb-5">
                        <AudioElement
                            v-if="audio.audioData.audio.length > 0 && !audioLoading"
                            :audioObject="audio"
                            :postAudioDataId="postData.postAudioDataId"
                            :postOwner="postData.userId"
                            :userData="userData"
                            :selectedAudioId="selectedAudioId"
                            :isGroupAdmin="isGroupAdmin"
                            :postType="postType"
                            :postData="postData"
                            :videoLoading="videoLoading"
                            :audioElementHasBegunPlaying="audioElementHasBegunPlaying"
                            :searchQuery="searchQuery"
                            :searchHighlightColor="searchHighlightColor"
                            :currentSegmentIndex="currentSegmentIndexForElement(audio.audioDataId)"
                            :anonymousMode="postData.anonymousMode || false"
                            @delete-selected-audio-element="deleteDialog"
                            @audioChanged="changeAudioElement"
                            @changeAudioSegment="changeAudioSegment"
                            @edit-caption="editCaption"
                            @saveEditAudioElementTitle="saveEditAudioElementTitle"
                            @update-specific-audio-data="getSpecificAudioData"
                            @update-selected-subtitle-language="updateSelectedSubtitleLanguage"
                        />
                    </v-container>
                </v-col>

                <v-col v-if="rightAudioData.length > 0" cols="12" md="6" lg="12" xl="12" class="hidden-xs-only hidden-lg-and-up ma-0 pr-0 py-0 justify-end">
                    <v-container fluid v-for="audio in rightAudioData" :key="audio.audioDataId" class="pt-0 px-0 pb-5">
                        <AudioElement
                            v-if="audio.audioData.audio.length > 0 && !audioLoading"
                            :audioObject="audio"
                            :postAudioDataId="postData.postAudioDataId"
                            :postOwner="postData.userId"
                            :userData="userData"
                            :selectedAudioId="selectedAudioId"
                            :isGroupAdmin="isGroupAdmin"
                            :postType="postType"
                            :postData="postData"
                            :videoLoading="videoLoading"
                            :audioElementHasBegunPlaying="audioElementHasBegunPlaying"
                            :searchQuery="searchQuery"
                            :searchHighlightColor="searchHighlightColor"
                            :currentSegmentIndex="currentSegmentIndexForElement(audio.audioDataId)"
                            :anonymousMode="postData.anonymousMode || false"
                            @delete-selected-audio-element="deleteDialog"
                            @audioChanged="changeAudioElement"
                            @changeAudioSegment="changeAudioSegment"
                            @edit-caption="editCaption"
                            @saveEditAudioElementTitle="saveEditAudioElementTitle"
                            @update-specific-audio-data="getSpecificAudioData"
                            @update-selected-subtitle-language="updateSelectedSubtitleLanguage"
                        />
                    </v-container>
                </v-col>

                <v-col v-if="audioLoading" cols="12" class="hidden-md-only hidden-sm-only">
                    <div v-for="i in 5" :key="i" class="my-0 pt-0 px-0 pb-5">
                        <v-skeleton-loader height="100" type="image" class="rounded-lg"></v-skeleton-loader>
                    </div>
                </v-col>

                <!-- Commented out by Scott Brady on July 10, 2024, along with the corresponding code below (ie import statement, component registration and computed property). -->
                <!-- This code was used to test vue-virtual-scrollers functionality in our site. It did not work, but with more time and effort we may be able to get it to work. -->
                <!-- If not, we have to write our own virtual scroller for the situation of using our own component, but more specifically, have it extend to show more content. -->
                <!-- vue-virtual-scroller documentation: https://github.com/Akryum/vue-virtual-scroller/blob/master/packages/vue-virtual-scroller/README.md -->
                <!-- <DynamicScroller :items="items" :key-field="'audioDataId'" :min-item-size="125" v-slot="{ item, index }" @update:visible-items="onVisibleItemsChange">
                    <v-col :key="index" cols="12" class="hidden-md-only hidden-sm-only my-0 pt-0 px-0 pb-5">
                        <AudioElement
                            v-if="item.audioData.audio.length > 0 && !audioLoading"
                            :audioObject="item"
                            :postAudioDataId="postData.postAudioDataId"
                            :postOwner="postData.userId"
                            :userData="userData"
                            :selectedAudioId="selectedAudioId"
                            :isGroupAdmin="isGroupAdmin"
                            :postType="postType"
                            :postData="postData"
                            :videoLoading="videoLoading"
                            :audioElementHasBegunPlaying="audioElementHasBegunPlaying"
                            :searchQuery="searchQuery"
                            :anonymousMode="postData.anonymousMode || false"
                            @delete-selected-contribution="deleteDialog"
                            @audioChanged="changeAudioElement"
                            @changeAudioElement="changeAudioSegment"
                            @edit-caption="editCaption"
                            @saveEditContributionTitle="saveEditContributionTitle"
                        />
                    </v-col>
                </DynamicScroller> -->

                <v-col v-for="audio in languageFilteredAudio" :key="audio.audioDataId" cols="12" class="hidden-md-only hidden-sm-only my-0 pt-0 px-0 pb-5">
                    <AudioElement
                        v-if="audio.audioData.audio.length > 0 && !audioLoading"
                        :audioObject="audio"
                        :postAudioDataId="postData.postAudioDataId"
                        :postOwner="postData.userId"
                        :userData="userData"
                        :selectedAudioId="selectedAudioId"
                        :isGroupAdmin="isGroupAdmin"
                        :postType="postType"
                        :postData="postData"
                        :videoLoading="videoLoading"
                        :audioElementHasBegunPlaying="audioElementHasBegunPlaying"
                        :searchQuery="searchQuery"
                        :searchHighlightColor="searchHighlightColor"
                        :currentSegmentIndex="currentSegmentIndexForElement(audio.audioDataId)"
                        :anonymousMode="postData.anonymousMode || false"
                        @delete-selected-audio-element="deleteDialog"
                        @audioChanged="changeAudioElement"
                        @changeAudioSegment="changeAudioSegment"
                        @edit-caption="editCaption"
                        @saveEditAudioElementTitle="saveEditAudioElementTitle"
                        @update-specific-audio-data="getSpecificAudioData"
                        @update-selected-subtitle-language="updateSelectedSubtitleLanguage"
                    />
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
// import { getFunctions, httpsCallable } from "firebase/functions";
import fb from "firebase/compat/app";
import firebase from "../../firebaseConfig.js";
import Alert from "./Alert.vue";
import VideoPlayer from "./VideoPlayer";
import AudioElement from "./AudioElement.vue";
import Report from "./Report.vue";
import UploadPhotoDialog from "./UploadPhotoDialog.vue";
import ShareDialog from "./ShareDialog.vue";
import { IconOverlapSkip, IconOverlapChop } from "./icons/overlapIcons";
import { CANONICAL_DEFAULTS } from "../../settingsDefaults.js";
// import VTT from "./VttDownload.vue";
// import { DynamicScroller, RecycleScroller } from 'vue-virtual-scroller';

// CANONICAL_DEFAULTS moved to the shared settingsDefaults.js (imported above) so ViewPost,
// PostView, and SettingsPage read one source and can't drift. See that file for the canon
// rationale (continuous + full caption audio for stored-preference-free sessions).

export default {
    name: "view-post",
    metaInfo() {
        return {
            title: typeof this.postData.title != 'undefined' ? `${this.postData.title} - tawq.in` : 'View Post - tawq.in',
        };
    },
    props: ["postId", "postType"],
    components: {
        Alert,
        VideoPlayer,
        AudioElement,
        Report,
        UploadPhotoDialog,
        ShareDialog,
        IconOverlapSkip,
        IconOverlapChop,
        // VTT,
        // DynamicScroller,
        // RecycleScroller,
    },
    data: function () {
        // userData is {} when signed out and can be null if an older persisted null is rehydrated
        // (see resetUserData in the store). Read settings through this local guard so a null can
        // never abort data() — which would blank the whole component.
        const ud = this.$store.state.userData || {};
        return {
            // Dialog pop ups
            deleteSelectedAudioElementDialog: false,
            showShareDialog: false,
            reportDialog: false,
            uploadPhotoDialog: false,
            vttDialog: false,
            senderSettingsActive: false, // true when post was opened via a share link with sender settings
            // END Dialog pop ups
            audioToDelete: null,
            menu: false,
            titleRules: [(v) => !!v || "Title is required", (v) => (v && v.length <= 50) || "Title must be 50 characters or less",],
            descriptionRules: [(v) => !!v || "Description is required", (v) => (v && v.length <= 750) || "Description must be 750 characters or less",],
            postMetadataHasChanged: false,
            postMetadataError: false,
            editPost: false,
            editPostMetadataLoading: false, // used to set editPostMetadata to a loading value so the user knows it is loading.
            letUserSeePost: false, // July 11, 2023: changed to false so that a user won't see a group post until we have confirmed they are a member of the group. This also mean a user will not see a public post until we have confirmed it is not a group post.
            viewLoading: true, // set to true on July 11, 2023. viewLoading will only be true on initial load, this solves the issue of VideoPlayer disappearing when updating metadata or anonymousMode.
            audioLoading: true,
            currentUserId: firebase.auth.currentUser.uid,
            postData: {},
            selectedAudioId: null, // used to highlight (outline) the currently playing AudioElement
            currentAudioElementIndex: null, // index of the currently selected AudioElement
            currentAudioElement: null, // this will be the currently selected AudioElement
            previousAudioSegment: null, // this will be the previous segment loaded into the audioPlayer
            currentAudioSegment: null, // this will be the current segment loaded into the audioPlayer
            nextAudioSegment: null, // this will be the next segment to be loaded into the audioPlayer
            vidOptions: {
                autoplay: false,
                controls: false,
                // controls: true,
                responsive: true,
                sources: {},
                techOrder: ["youtube", "html5"],
                textTrackSettings: false,
                html5: {
                    nativeTextTracks: false,
                    nativeControlsForTouch: false,
                    nativeAudioTracks: false,
                },
                youtube: {
                    enablejsapi: 1,
                    fs: 0,
                    playsinline: 1,
                    cc_load_policy: 0, // YouTube auto captions - OFF
                    // cc_load_policy: 1, // YouTube auto captions - ON
                    disablekb: 1,
                    ytControls: 0, // Disable YouTube control bar
                    // ytControls: 1, // Enable YouTube control bar
                    // origin: "https://tawq.in" || window.location.orgin, // maybe can use this instead of having to comment out below for testing?
                    // origin: "https://tawq.in", // uncomment for deploy to dev/main
                    origin: window.location.origin, // uncomment for testing
                    modestbranding: 1,
                    start: 0,
                    // widget_referrer: "https://tawq.in",
                },
            },
            audioData: [],
            // showCaptions was added to code on May 21, 2024 by Scott Brady. Any user who created an account before then will not have it in their userData unless they have opened their Settings.
            showCaptions: (ud.showCaptions !== undefined && ud.showCaptions !== null) ? ud.showCaptions : CANONICAL_DEFAULTS.showCaptions, // if showCaptions does not exist fall through to canon. Only used in computed property of VideoPlayer.vue to show/hide captions
            playbackRate: CANONICAL_DEFAULTS.playbackRate,
            playbackStyle: (ud.playbackStyle !== undefined && ud.playbackStyle !== null) ? (ud.playbackStyle ? 'continuous' : 'pauseplay') : CANONICAL_DEFAULTS.playbackStyle,
            // playbackStyleAudioFirst: false,
            // When playbackStyle is 'pauseplay', this determines whether the audio plays before or after the video. IE if true, when audio segment start is reached, video will pause and audio will play. If false, when audio segment start + duration is reached, video will pause and audio will play.
            playbackStyleAudioFirst: (typeof ud.playbackStyleAudioFirst === 'undefined') ? true : ud.playbackStyleAudioFirst, // if audio first value does not exist set to default value of true.
            // playbackStyleSameStart: false,
            // When playbackStyle is 'continuous', this determines whether the audio will be cut off or not. IE if true, when audio segment duration overlaps next segment audio start, it will continue to play. If false, when audio segment duration overlaps next segment audio start, audio will cut off and play next audio segment.
            playbackStyleSameStart: (typeof ud.playbackStyleSameStart === 'undefined') ? true : ud.playbackStyleSameStart, // if same start value does not exist set to default value of true.

            previousAudioPlayer: null, // used to load the previous audio segment into the audioPlayer
            audioPlayer: new Audio(), // main audioPlayer, used to PLAY the current audio segment
            nextAudioPlayer: new Audio(), // used to load the next audio segment into the audioPlayer

            // viewAdvancedControls: true, // this is used to pop up  the dialog that allows the user to use advanced controls (includes: playback style, turning on/off captions, caption audio volume, time skipping forward/backward, speed of video/audio playback, etc.). Added by Scott Brady on October 9, 2024
            viewAdvancedControls: false, // this is used to pop up  the dialog that allows the user to use advanced controls (includes: playback style, turning on/off captions, caption audio volume, time skipping forward/backward, speed of video/audio playback, etc.). Added by Scott Brady on October 9, 2024
            advancedControlsOptionsClicked: false, // this is used to show selectable options for advanced controls (playback style, volume, speed, etc.)
            delayAdvancedOptionsClickTimer: null,
            previousClickedTimer: null,
            rewindForwardTimer: null,
            advancedControl: '',
            videoIsPlaying: false, //

            displayCurrentSubtitle: false,
            captionVolume: (ud.captionAudio !== undefined && ud.captionAudio !== null) ? (ud.captionAudio ? 1.0 : 0.0) : CANONICAL_DEFAULTS.captionVolume,
            lastCaptionVolume: 1.0, // last audible (>0) caption volume; what the mute toggle restores on unmute
            viewFullDescription: false,
            descriptionWordCount: 0,
            // scrollOptions: { // commented out on July 27, 2023. Not sure what this does!
            //     duration: 100,
            //     offset: 0,
            //     easing: 'easeInOutCubic'
            // },
            filter: {
                languageSelected: "All",
                languageCodes: ["All"],
            },
            filterMenu: false,
            searchQuery: "",
            searchHighlightColor: localStorage.getItem('tawqSearchColor') || '#FF5722',
            searchColorOptions: ['#FF5722', '#2196F3', '#9C27B0', '#E53935'],
            showColorPicker: false,
            postViewedCounter: null,
            isGroupAdmin: null,
            videoLoading: true, // Used to put the video player in, or take out of, a loading state. When true the vjs-loading-spinner will show, else it's hidden.
            audioElementHasBegunPlaying: false,
            audioCanPlay: false, // Used to make sure audio is ready to play when marker is reached.
            audioElementOrSegmentClicked: false,

            showLanguageSelectionMenu: false,
            subtitlesByLanguageSelected: false,
            selectedSubtitleLanguage: null,
            subtitleByAudioElementId: {},
            dismissedOverlapHint: false,
            dismissedDeadAirHint: false, // once-per-session, mirrors dismissedOverlapHint; see showDeadAirHint
            videoIsMuted: false,
            videoVolume: 1.0, // tracks video player volume for share URL
            statusBarDismissedKey: null, // snapshot of settingsAnomaliesKey at the moment the status bar was dismissed; the bar reappears automatically if the set of anomalies changes
        };
    },
    methods: {
        deleteDialog: function (audioObject) {
            this.deleteSelectedAudioElementDialog = true;
            this.audioToDelete = audioObject;
        },
        setSearchColor: function(color) {
            this.searchHighlightColor = color;
            localStorage.setItem('tawqSearchColor', color);
        },
        currentSegmentIndexForElement: function(audioDataId) {
            // Returns the currently-playing segment index, but ONLY for the active AudioElement.
            // Other AudioElements receive -1 so their segments never light up.
            if (audioDataId !== this.selectedAudioId) return -1;
            return this.playingSegmentIndex;
        },
        scrollPlayingSegmentIntoView: function() {
            // Gentle scroll: only fire when no search is active, and only scrolls if off-screen.
            if (this.searchQuery && this.searchQuery.trim()) return;
            if (!this.currentAudioSegment || typeof this.currentAudioSegment.index !== 'number') return;
            this.$nextTick(() => {
                const el = document.querySelector(
                    `[data-segment-index="${this.currentAudioSegment.index}"]`
                );
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }
            });
        },
        updateSearchMarkers: function(query) {
            if (!this.$refs.vidPlayer) return;
            const q = (query || '').trim().toLowerCase();

            // Always clear previous search markers first so keystrokes don't stack them up.
            // Segment markers (class: 'markers-style' or null) are preserved because
            // removeSearchMarkers only removes class 'search-match-marker'.
            this.$refs.vidPlayer.removeSearchMarkers();

            if (!q) return;

            // Build match markers across all audio elements
            const color = this.searchHighlightColor || '#FF5722';
            const matchMarkers = [];
            const seenKeys = new Set(); // dedupe matches at the same timestamp from same contribution
            this.audioData.forEach(ae => {
                const audio = ae.audioData.audio;
                const username = ae.audioData.username || 'anonymous';
                if (!Array.isArray(audio)) return;

                // Collect all subtitle arrays for this contribution: primary + every language variant
                const subtitleArrays = [];
                if (Array.isArray(ae.audioData.subtitles)) {
                    subtitleArrays.push(ae.audioData.subtitles);
                }
                if (ae.audioData.subtitlesByLanguage && typeof ae.audioData.subtitlesByLanguage === 'object') {
                    for (const arr of Object.values(ae.audioData.subtitlesByLanguage)) {
                        if (Array.isArray(arr)) subtitleArrays.push(arr);
                    }
                }

                audio.forEach((seg, i) => {
                    // A segment matches if ANY of its subtitle arrays contains the query
                    for (const arr of subtitleArrays) {
                        const sub = arr[i];
                        if (sub && sub.subtitle && sub.subtitle.toLowerCase().includes(q)) {
                            // Dedupe: one marker per (contribution + segment) even if multiple
                            // language variants match the same segment
                            const key = `${ae.audioDataId || username}:${i}`;
                            if (seenKeys.has(key)) break;
                            seenKeys.add(key);

                            const snippet = sub.subtitle.length > 40 ? sub.subtitle.substring(0, 40) + '…' : sub.subtitle;
                            matchMarkers.push({
                                time: seg.audioStart,
                                text: `@${username}: ${snippet}`,
                                class: 'search-match-marker',
                            });
                            break;
                        }
                    }
                });
            });
            // Inject marker color into document so .search-match-marker picks it up
            let styleEl = document.getElementById('tawq-search-marker-style');
            if (!styleEl) {
                styleEl = document.createElement('style');
                styleEl.id = 'tawq-search-marker-style';
                document.head.appendChild(styleEl);
            }
            styleEl.textContent = `.search-match-marker { background-color: ${color} !important; width: 4px !important; opacity: 0.9; }`;

            this.$refs.vidPlayer.addSearchMarkers(matchMarkers);

            // Inline-style override: the markers plugin applies its default markerStyle
            // (tawq amber/green) as inline CSS on every marker element, which beats our
            // injected <style> tag. Walk the just-added elements and set the color directly.
            this.$nextTick(() => {
                document.querySelectorAll('.search-match-marker').forEach(el => {
                    el.style.backgroundColor = color;
                });
            });
        },
        deleteAudioElement: async function(val){
            if(!val) {
                this.deleteSelectedAudioElementDialog = false;
            } else {
                /* delete metadata first, then the audio file */
                this.deleteAudioMetadata(this.audioToDelete.audioData.userId,this.audioToDelete.audioDataId);
                this.deleteAudioFilesFromStorage(this.audioToDelete.audioDataId);
                this.deleteSelectedAudioElementDialog = false;
            }
        },
        triggerFileUpload: function(){
            if (!this.isUsersPost()){
                this.$store.commit('alertUser', { show: true, text: `You do not have permissions to upload an image for this post.`, type: 'snackbar' });
                return;
            }
            this.uploadPhotoDialog = true;
        },
        updateAnonymousMode: async function () {
            firebase.db.collection(this.postType === 'group' ? 'groupPosts' : 'publicPosts').doc(this.postId).update({ anonymousMode: !this.postData.anonymousMode }, {merge: true}).then(()=>{
                this.postType === 'group' ? this.getGroupPostData() : this.getPostData();
            }).catch((error)=>{
                console.error("error: ", error);
                this.$store.commit('alertUser', { show: true, text: `Something went wrong updating anonymousMode ${error}`, type: 'snackbar' });
            });
        },
        isUsersPost: function() {
            return this.postData.userId == firebase.auth.currentUser.uid;
        },
        editPostMetadata: function () {
            if (this.postMetadataHasChanged && this.$refs.editDescription.valid && this.$refs.editTitle.valid) {
                this.editPostMetadataLoading = true;
                this.audioLoading = true;
                // this.editPost = false; // October 25, 2023 - moved this further down in the code so that it will show loading while metadata updates
                this.$http.put(`/post/metadata/${this.postType}/${this.postId}`, {
                    description: this.postData.description,
                    title: this.postData.title,
                    audioDataId: this.postData.postAudioDataId,
                    pinnedPost: (this.postType == 'public' && this.postData.pinnedPost) ? this.postData.pinnedPost : false,
                }).then((/*response*/) => {
                    this.$store.commit('alertUser', { show: true, text: `Successfully Changed Post Metadata.`, type: 'snackbar' });
                    if(this.postData.postType == "public"){
                        this.getPostData();
                    } else {
                        this.getGroupPostData();
                    }
                    this.getMainPostAudioData(); // retrieve the updated postAudioData audioSegment (the main one for the post);
                    this.editPost = false;
                }).catch((error) => {
                    console.error("Error updating post metadata", error.message);
                    this.$store.commit('alertUser', { show: true, text: `Error Changing Post.`, type: 'snackbar' });
                    this.editPostMetadataLoading = false;
                    this.audioLoading = false;
                    this.editPost = false;
                });
            } else {
                this.editPost = false;
            }
        },
        // permissionToDelete: function() { // commented out on July 27, 2023. Not sure this is being used.
        //     if (this.isUsersPost() && !this.homefeed){
        //         return true;
        //     } else {
        //         return false;
        //     }
        // },
        thumbnailUploadSuccess: function(){
            this.postType === 'group' ? this.getGroupPostData() : this.getPostData();
            this.$store.commit('alertUser', { show: true, text: `Successfully changed post thumbnail.`, type: 'snackbar' });
        },
        deleteAudioFilesFromStorage: function (contributionId) { // delete the audiofile from audioSegments/postId/audioDataId
            const audioDataRef = firebase.storage.ref(`audioSegments/${this.postData.postId}/${contributionId}`);
            audioDataRef.list().then((res) => {
                res.items.forEach(function (itemRef) {
                    /* retrieve the file names of each segment, so that we can delete each file
                    individually from the storage bucket. GCS doesn't allow us to delete the full bucket */
                    let filename = itemRef.name;
                    let audioRef = audioDataRef.child(filename);
                    audioRef.delete().then(() => {
                        return true;
                    }).catch(function (error) {
                        console.error(`Error Deleting Audio Segment File ${filename} from Storage: `, error);
                        return false;
                    });
                });
            });
        },
        deleteAudioMetadata: function (userId, contributionId) {
            const postRef = firebase.db.collection(this.postData.postType === 'public' ? "publicPosts" : "groupPosts").doc(this.postId);
            const contributionsRef = postRef.collection("contributions").doc(userId);
            // const analyticsRef = firebase.db.collection("admin").doc("analytics");

            contributionsRef.update({audioDataId: fb.firestore.FieldValue.arrayRemove(contributionId),}).catch((error) => {
                this.$store.commit('alertUser', { show: true, text: `Error Deleting Contribution.`, type: 'snackbar' });
                console.error("Error removing audio metadata in posts ref", error);
            });

            postRef.update({"postAnalytics.total_contributions": fb.firestore.FieldValue.increment(-1)});

            // analyticsRef.update({total_contributions: fb.firestore.FieldValue.increment(-1)}).catch((err) => {
            //     console.log("Error updating the total contribution field in the analytics ref.", err);
            // });

            // delete file from audioSegments/audioData
            const audioSegmentsRef = firebase.db.collection("audioSegments").doc(contributionId);
            audioSegmentsRef.delete().then(() => {
                this.$store.commit('alertUser', { show: true, text: `Successfully deleted contribution.`, type: 'snackbar' });
                this.getAudioData();
            }).catch((error) => {
                console.error("Error removing the audioSegments document: ", error);
            });
        },
        editCaption: function (caption) {
            let audioSegmentsRef = firebase.db.collection("audioSegments").doc(caption.audioSegId);
            audioSegmentsRef.get().then((snapshot) =>{
                if(snapshot.exists){
                    var subtitles = snapshot.data().subtitles;
                    snapshot.data().subtitles.forEach((subtitle, index) => {
                        if(subtitle.audioSegId == caption.caption.audioSegId){
                            if (this.postType === 'group') {
                                subtitles[index] = {subtitle: caption.caption.subtitle, audioSegId: subtitle.audioSegId, audioSegFeedback: caption.caption.audioSegFeedback};
                            } else {
                                subtitles[index] = {subtitle: caption.caption.subtitle, audioSegId: subtitle.audioSegId};
                            }
                        }
                    });
                    audioSegmentsRef.update({ subtitles :  subtitles}, {merge: true});
                }
            })
        },
        saveEditAudioElementTitle: async function (audioObject) {
            let audioSegmentsRef = firebase.db.collection("audioSegments").doc(audioObject.audioObject.audioDataId);
            let res = await audioSegmentsRef.update({title: audioObject.audioObject.audioData.title}, {merge: true});
            // The above response variable, res, returns undefined, but might need to be used to make sure the function
            // is done running and saves the data correctly. For now it should work just fine.
        },
        addNewAudio: function () {
            this.$router.push({
                name: "contribution",
                query: {
                    pid: this.postData.postId,
                    postType: this.postData.postType,
                },
                params: { audioData: this.audioData, },
            });
        },
        compactLanguageLabel: function (label) {
            // Compact a language label for the filter chip.
            // "Arabic (Oman)"  -> "Arabic"
            // "한국어 | Korean" -> "한국어"
            // Anything else stays as-is.
            if (typeof label !== 'string') return label;
            if (label.includes('|')) return label.split('|')[0].trim();
            if (label.includes('(')) return label.split('(')[0].trim();
            return label;
        },
        clearLanguageFilter: function () {
            // Called when the user taps the X on the active-filter chip.
            // The @click.stop.prevent modifiers on the v-icon handle event bubbling;
            // this just resets state.
            this.filter.languageSelected = 'All';
            this.filterMenu = false;
        },
        onHasBegunPlaying: function (playing) {
            this.videoIsPlaying = playing;
            if (playing) this.audioElementHasBegunPlaying = true;
        },
        updateSelectedSubtitleLanguage: function (language, subtitlesByLanguageSelected, audioObject) {
            this.$set(this.subtitleByAudioElementId, audioObject.audioDataId, {
                language, subtitlesByLanguageSelected,
            });

            if (audioObject.audioDataId !== this.currentAudioElement.audioDataId) return;

            this.$refs.vidPlayer.hasTextTrack() && this.$refs.vidPlayer.removeCue(true); // IMPORTANT: When switching subtitles mid-play, re-sync cues immediately

            this.subtitlesByLanguageSelected = subtitlesByLanguageSelected;
            this.selectedSubtitleLanguage = language;

            let idx = this.currentAudioSegment.index;

            if ((idx - 1) >= 0) this.previousAudioSegment = this.applySegmentMetadata(this.currentAudioElement.audioData.audio[idx - 1], idx - 1);
            this.currentAudioSegment = this.applySegmentMetadata(this.currentAudioElement.audioData.audio[idx], idx);
            if ((idx + 1) < this.currentAudioElement.audioData.audio.length) this.nextAudioSegment = this.applySegmentMetadata(this.currentAudioElement.audioData.audio[idx + 1], idx + 1);

            // Optional but recommended: pause audio so your timeUpdate guards don't block re-arming
            // if (!this.audioPlayer.paused) {
            //     this.audioPlayer.pause();
            //     // this.audioEnded(); // if this is how you advance segment indices / state
            //     // Force a sync using the *current* video time
            //     const now = this.$refs.vidPlayer.playerGetTime?.() ?? 0;
            //     this.timeUpdate(Number(now.toFixed(1)));
            // }

            this.$refs.vidPlayer.removeMarkers();
            this.$refs.vidPlayer.placeMarkers(this.currentAudioElement.audioData.audio, (this.subtitlesByLanguageSelected ? this.currentAudioElement.audioData.subtitlesByLanguage[this.selectedSubtitleLanguage] : this.currentAudioElement.audioData.subtitles), this.currentAudioElement.audioData.username);
        },
        applySegmentMetadata: function (segment, index) {
            if (!segment) return segment;
            segment.index = index;
            const subtitle = this.subtitlesByLanguageSelected ? (this.currentAudioElement?.audioData?.subtitlesByLanguage[this.selectedSubtitleLanguage]?.[index]) : (this.currentAudioElement?.audioData?.subtitles?.[index]);
            if (subtitle) {
                segment.subtitle = subtitle.subtitle;
                segment.subtitleSegment = subtitle.audioSegId;
            }
            return segment;
        },
        loadAndPreloadFirstAudioData: async function () {
            // console.log("2. loadAndPreloadFirstAudioData called");
            // This function is called on initial load of the page and anytime a new AudioElement is clicked.
            // It will load the first AudioSegment into the audioPlayer and if playbackStyle is pauseplay, it
            // will load in the second AudioSegment, otherwise, playbackStyle is continuous, and it will load
            // in the next, or one available after the overlapping segments.

            // set this.currentAudioSegment to the first AudioSegment in this.currentAudioElement
            this.currentAudioSegment = this.applySegmentMetadata(this.currentAudioElement.audioData.audio[0], 0);
            if (this.currentAudioSegment.audioStart === 0) this.audioElementHasBegunPlaying = true;

            // preload the main (current) audioPlayer so we don't miss any events (browser can fetch aggressively)
            this.addAudioPlayerEventListeners();
            this.audioPlayer.preload = 'auto';
            this.audioPlayer.src = this.currentAudioSegment.audioURL;
            this.audioPlayer.load();

            const hasNextSegment = this.currentAudioElement.audioData.audio.length > 1;
            if (!hasNextSegment) {
                this.nextAudioSegment = null;
                this.nextAudioPlayer = null;
                return;
            }

            const nextIndex = (this.playbackStyle === 'continuous' && this.playbackStyleSameStart)
                ? await this.getNextAvailableIndexAfterOverlap(0, 1) // check for overlaps and return the next available AudioSegment index, or null if not available.
                : 1; // because the video player is paused we won't miss any segments, set next available AudioSegment as second in list, or index 1.

            if (nextIndex === null) {
                // All AudioSegments in this.currentAudioElement (currently selected AudioElement) overlap, unable to set nextAudioSegment.
                this.nextAudioSegment = null;
                this.nextAudioPlayer = null;
                return;
            }

            this.nextAudioSegment = this.applySegmentMetadata(this.currentAudioElement.audioData.audio[nextIndex], nextIndex);
            this.nextAudioPlayer = new Audio();
            this.nextAudioPlayer.volume = this.captionVolume; // preserve caption volume on the preloaded player
            this.nextAudioPlayer.preload = 'auto';
            this.nextAudioPlayer.src = this.nextAudioSegment.audioURL;
            this.nextAudioPlayer.load();
        },
        loadAndPreloadNextAudioData: async function () {
            this.previousAudioSegment = this.currentAudioSegment;
            this.currentAudioSegment = this.nextAudioSegment;

            this.previousAudioPlayer = this.audioPlayer; // keep previous player loaded for quick replay
            this.audioPlayer = this.nextAudioPlayer; // move preloaded audio into the active slot
            this.audioPlayer.volume = this.captionVolume; // preserve caption volume across segment transitions
            this.nextAudioPlayer = null;
            this.addAudioPlayerEventListeners();

            if (!this.currentAudioSegment) return this.nextAudioSegment = null;

            const hasMoreSegments = (this.currentAudioElement.audioData.audio.length - 1) >= (this.currentAudioSegment.index + 1);
            if (!hasMoreSegments) return this.nextAudioSegment = null;

            const nextIndex = (this.playbackStyle === 'continuous' && this.playbackStyleSameStart)
                ? await this.getNextAvailableIndexAfterOverlap(this.currentAudioSegment.index, this.currentAudioSegment.index + 1)
                : this.currentAudioSegment.index + 1;

            if (nextIndex === null) return this.nextAudioSegment = null; // All remaining AudioSegments overlap, nothing to queue.

            this.nextAudioSegment = this.applySegmentMetadata(this.currentAudioElement.audioData.audio[nextIndex], nextIndex);
            this.nextAudioPlayer = new Audio();
            this.nextAudioPlayer.volume = this.captionVolume; // preserve caption volume on the preloaded player
            this.nextAudioPlayer.preload = 'auto';
            this.nextAudioPlayer.src = this.nextAudioSegment.audioURL;
            this.nextAudioPlayer.load();
        },
        loadAndPreloadAudioDataAfterClick: async function (index) {
            const audio = this.currentAudioElement.audioData.audio;
            const hasPrevious = index > 0;
            const hasNext = (audio.length - 1) >= (index + 1);

            this.previousAudioPlayer = null;
            this.audioPlayer = new Audio();
            this.audioPlayer.volume = this.captionVolume; // preserve caption volume across segment changes
            this.nextAudioPlayer = null;

            this.previousAudioSegment = hasPrevious ? this.applySegmentMetadata(audio[index - 1], index - 1) : null;

            this.currentAudioSegment = this.applySegmentMetadata(audio[index], index);
            this.audioPlayer.preload = 'auto';
            this.audioPlayer.src = this.currentAudioSegment.audioURL;
            this.audioPlayer.load();
            this.addAudioPlayerEventListeners();

            if (hasNext) {
                const nextIndex = (this.playbackStyle === 'continuous' && this.playbackStyleSameStart)
                    ? await this.getNextAvailableIndexAfterOverlap(index, index + 1)
                    : index + 1;

                if (nextIndex !== null) {
                    this.nextAudioSegment = this.applySegmentMetadata(audio[nextIndex], nextIndex);
                    this.nextAudioPlayer = new Audio();
                    this.nextAudioPlayer.volume = this.captionVolume; // preserve caption volume on the preloaded player
                    this.nextAudioPlayer.preload = 'auto';
                    this.nextAudioPlayer.src = this.nextAudioSegment.audioURL;
                    this.nextAudioPlayer.load();
                } else {
                    // All AudioSegments in this.currentAudioElement overlap; nothing to queue.
                    this.nextAudioSegment = null;
                }
            } else {
                this.nextAudioSegment = null;
            }
        },
        getNextAvailableIndexAfterOverlap: async function (index, indexToCheck) {
            const audio = this.currentAudioElement.audioData.audio;
            const len = audio.length;

            if (indexToCheck >= len) return null;

            const baseEnd = audio[index].audioEnd ?? (audio[index].audioStart + audio[index].audioDur);

            for (let i = indexToCheck; i < len; i++) {
                if (baseEnd < audio[i].audioStart) {
                    return i;
                }
            }

            return null;
        },
        enableControls: function () {
            this.$refs.vidPlayer.getMarkers().length <= 0 && this.$refs.vidPlayer.placeMarkers(this.currentAudioElement.audioData.audio, this.currentAudioElement.audioData.subtitles, this.currentAudioElement.audioData.username)
        },
        pauseAudioPlayer: function (val) {
            // TODO: Figure out a way to solve the issue of not being able to pause/play audio, when in pauseplay mode!!!
            // Idea: if video paused and audio playing, end segment when clicked.
            if(this.audioPlayer.duration > 0 && !this.audioPlayer.paused) {
                if (this.playbackStyle == "continuous") {
                    this.audioPlayer.pause();
                }
            }
        },
        playAudioPlayer: function (val) {
            // console.log("playAudioPlayer");
            if (!this.audioPlayer.ended && this.audioPlayer.duration > 0 && this.audioPlayer.paused) {
                if(this.playbackStyle == "continuous") {
                    this.audioPlayer.play();
                } else {
                    // this.$refs.vidPlayer.playerPlay();
                    this.$refs.vidPlayer.playerPause();
                    this.audioPlayer.play();
                }
            } else if (!this.audioPlayer.ended && this.audioPlayer.duration > 0 && !this.audioPlayer.paused) {
                if(this.playbackStyle == "continuous") {
                    this.audioPlayer.play();
                } else {
                    this.$refs.vidPlayer.playerPause();
                    this.audioPlayer.pause();
                }
            }
        },
        addAudioPlayerEventListeners: function () {
            // console.log("3. addAudioPlayerEventListeners called");
            this.audioPlayer.addEventListener('canplaythrough', this.audioCanPlayThrough/*, {once: true}*/);
            this.audioPlayer.addEventListener("ended", this.audioEnded/*, {once: true}*/);
        },
        removeAudioPlayerEventListeners: function () {
            this.audioPlayer.removeEventListener("canplaythrough", this.audioCanPlayThrough);
            this.audioPlayer.removeEventListener("ended", this.audioEnded/*, {once: true}*/);
        },
        audioCanPlayThrough: function () {
            // console.log("4. audioCanPlayThrough fired");
            this.audioCanPlay = true;

            // if (this.videoLoading) { console.log("First time through video should be loading"); this.videoLoading = false; }
            if (this.videoLoading) this.videoLoading = false;
            // When videoLoading is set to false here, the function videoLoading() in VideoPlayer.vue's watch property
            // gets fired. Which in turn hides the loadingSpinner, shows the bigPlayButton, if necessary, updates the
            // markers and sets player controls to true. When player.controls(true) the .on("controlsenabled") listener
            // gets fired. This will playerPlay(), play the video, the video player in VideoPlayer.vue and $emit("controlsEnabled") back
            // here to ViewPost.vue, which will call $refs.vidPlayer.placeMarkers();
        },
        audioEnded: function (/*event*/) {
            this.audioCanPlay = false;

            (this.playbackStyle === "pauseplay") && this.$refs.vidPlayer.playerPlay();
            this.$refs.vidPlayer.hasTextTrack() && this.$refs.vidPlayer.removeCue(true);
            // Capture whether another segment was queued BEFORE we advance. loadAndPreloadNextAudioData
            // promotes nextAudioSegment into currentAudioSegment and then, if the promoted segment is the
            // last one, sets nextAudioSegment = null. So after advancing we can no longer use
            // "nextAudioSegment == null" to mean "we are finished" — the final segment still needs to play.
            const hadQueuedSegment = this.nextAudioSegment != null;
            hadQueuedSegment && this.loadAndPreloadNextAudioData();
            this.displayCurrentSubtitle = false;
                // If nothing was queued, playback for this element is finished — in sameStart mode a null
                // nextAudioSegment means all remaining segments are intentionally skipped; in every other
                // mode it only happens at the last segment. Tear down so timeUpdate can't retrigger.          
                if (!hadQueuedSegment && this.currentAudioSegment != null) {
                this.previousAudioSegment = this.currentAudioSegment;
                this.currentAudioSegment = null;
                this.removeAudioPlayerEventListeners();
            }
        },
        near: function (a, b, eps = 0.3) {
            return Math.abs(a - b) <= eps;
        },
        timeUpdate: function (time) {
            if (this.videoLoading || !this.currentAudioSegment) return;

            const seg = this.currentAudioSegment;

            const start = seg.audioStart;
            const end = seg.audioEnd ?? (seg.audioStart + seg.audioDur);

            // Always keep segment metadata fresh (optional)
            // seg.subtitle should already be correct via applySegmentMetadata

            // If we are at the cue moment, always place the cue
            const reached = (x) => time >= x - 0.3;   // fires on arrival OR overshoot, not only inside a ±0.3 window
            const cueMoment =
                (this.playbackStyle === "pauseplay" && this.playbackStyleAudioFirst && reached(start)) ||
                (this.playbackStyle === "pauseplay" && !this.playbackStyleAudioFirst && reached(start + seg.audioDur)) ||
                (this.playbackStyle === "continuous" && reached(start));

            if (cueMoment) {
                this.$refs.vidPlayer.addCue(this.$refs.vidPlayer.playerGetTime(), end, seg.subtitle, (this.subtitlesByLanguageSelected ? (this.selectedSubtitleLanguage.split('-')[1].trim()) : this.displayLang));
            }

            // Overlap cutoff — continuous mode with Skip Overlap OFF. The setting's contract is
            // "end the current audio segment early to play the next." When the queued segment's
            // start time arrives while the current segment's audio is still playing, cut the
            // current audio and let audioEnded() promote the queued segment; the cueMoment check
            // below starts it on the next tick. Without this clause, tail-overlapping segments
            // play on top of each other — the original cutoff lived in the old timeUpdate and
            // was lost when it was commented out during the refactor.
            if (this.playbackStyle === "continuous" && !this.playbackStyleSameStart
                && this.nextAudioSegment != null
                && !this.audioPlayer.paused
                && reached(this.nextAudioSegment.audioStart)) {
                this.audioPlayer.pause();
                this.audioEnded();
                return;
            }

            // Only decide to play/pause audio if audio isn't already playing (prevents overlaps)
            if (!this.audioPlayer.paused) return;

            if (cueMoment) {
                if (this.playbackStyle === "pauseplay") this.$refs.vidPlayer.playerPause();
                this.audioPlayer.play();
                this.displayCurrentSubtitle = true;
            }
        },
        videoPlayerSeeked: function (time) {
            // console.log("videoPlayerSeeked: ", time);
            if (this.videoLoading/*|| this.currentAudioSegment.audioStart == 0 && this.audioPlayer.paused*/) return;
            if (this.audioElementOrSegmentClicked) return this.audioElementOrSegmentClicked = false;
            if (this.$refs.vidPlayer.playerIsPaused()) this.$refs.vidPlayer.playerPlay();
            this.videoLoading = true;
            !this.audioPlayer.paused && this.audioPlayer.pause();
            this.removeAudioPlayerEventListeners();
            this.$refs.vidPlayer.hasTextTrack() && this.$refs.vidPlayer.removeCue(true); //this.$refs.vidPlayer.removeTextTrack();

            [this.previousAudioSegment, this.currentAudioSegment, this.nextAudioSegment] = [null, null, null];
            // console.log("reset audio player values");

            // let nextAudioSegmentIndex = this.currentAudioElement.audioData.audio.findIndex((data) => Math.ceil(data.audioStart) >= Math.floor(time));
            // console.log("beginning check for next audio segment");
            let nextAudioSegmentIndex = this.currentAudioElement.audioData.audio.findIndex((data) => {
                if (this.playbackStyle === "pauseplay" && !this.playbackStyleAudioFirst) {
                    if (Math.abs((data.audioStart + data.audioDur) - time) <= 0.3) return true;
                    if ((data.audioStart + data.audioDur) >= time) return true;
                } else {
                    if (Math.abs(data.audioStart - time) <= 0.3) return true;
                    if (data.audioStart >= time) return true;
                }
            });
            // console.log("nextAudioSegmentIndex: ", nextAudioSegmentIndex);
            // console.log("last index: ", this.currentAudioElement.audioData.audio.length - 1);
            if (nextAudioSegmentIndex == -1) {
                let index = this.currentAudioElement.audioData.audio.length - 1;
                // this.audioPlayer.src = this.currentAudioElement.audioData.audio[index].audioURL;
                // this.audioPlayer.load();
                this.previousAudioSegment = this.currentAudioElement.audioData.audio[index];
                this.previousAudioSegment.index = index;
                this.previousAudioSegment.subtitle = this.currentAudioElement.audioData.subtitles[index].subtitle;
                this.previousAudioSegment.subtitleSegment = this.currentAudioElement.audioData.subtitles[index].audioSegId;
                return this.videoLoading = false;
            }

            this.loadAndPreloadAudioDataAfterClick(nextAudioSegmentIndex);
        },
        changeAudioSegment: function (data) {
            //  this function is ran only when a audioSegment is clicked. It will update the markers if necessary and playSelectedAudioSegment
            this.audioElementOrSegmentClicked = true;
            this.videoLoading = true;

            !this.audioPlayer.paused && this.audioPlayer.pause(); // if the player is NOT paused then pause, else nothing;
            this.removeAudioPlayerEventListeners();
            this.$refs.vidPlayer.hasTextTrack() && this.$refs.vidPlayer.removeCue(true); //this.$refs.vidPlayer.removeTextTrack();


            if (this.selectedAudioId === data.audioId) {
                this.$refs.vidPlayer.playerSetTime(data.audioSeg.audioSeg.audioStart); // set the start time then jump to loadAndPreloadAudioData function
            } else {
                // formatAE refers to the new AudioElement data, updated to the correct format, that will be set to this.currentAudioElement
                let formatAE = {
                    audioData: data.audioData,
                    audioDataId: data.audioId,
                };
                this.currentAudioElement = formatAE;

                const pref = this.subtitleByAudioElementId[data.audioId];
                this.subtitlesByLanguageSelected = pref ? pref.subtitlesByLanguageSelected : false;
                if (this.subtitlesByLanguageSelected) {
                    this.selectedSubtitleLanguage = pref.language;
                }

                this.selectedAudioId = data.audioId;

                this.$refs.vidPlayer.removeMarkers();
                this.$refs.vidPlayer.playerSetTime(data.audioSeg.audioSeg.audioStart);
                this.$router.replace({path: this.$route.name, query: { pid: this.postData.postId, postType: this.postData.postType, aid: this.selectedAudioId}});

                this.audioElementHasBegunPlaying = false; // Do I need this???
            }
            this.loadAndPreloadAudioDataAfterClick(data.audioSeg.index);
        },
        changeAudioElement: function (audioData, audioId) {
            // This function is called Everytime the play button on an AudioElement is clicked.

            if (this.selectedAudioId === audioId && !this.audioElementHasBegunPlaying) return; // Video player was set to auto play after initial loading is done. By Scott Brady on May 10, 2023. This if statement shouldn't/rarely happen.

            this.audioElementOrSegmentClicked = true;
            this.videoLoading = true;

            !this.audioPlayer.paused && this.audioPlayer.pause();
            this.removeAudioPlayerEventListeners();
            this.$refs.vidPlayer.hasTextTrack() && this.$refs.vidPlayer.removeCue(true); //this.$refs.vidPlayer.removeTextTrack();

            if (this.selectedAudioId === audioId && this.audioElementHasBegunPlaying) {
                this.audioElementHasBegunPlaying = false;
                this.$refs.vidPlayer.playerSetTime(0.0);
            } else if (this.selectedAudioId != audioId && !this.audioElementHasBegunPlaying) {
                // This else if statement should only happen if the user presses the play button of an AudioElement right after initial
                // loading is done, but before auto play has started. Shouldn't happen very often, if at all!!!
                this.$refs.vidPlayer.removeMarkers();
                this.currentAudioElement = {
                    audioDataId: audioId,
                    audioData: audioData,
                };

                const pref = this.subtitleByAudioElementId[audioId];
                this.subtitlesByLanguageSelected = pref ? pref.subtitlesByLanguageSelected : false;
                if (this.subtitlesByLanguageSelected) {
                    this.selectedSubtitleLanguage = pref.language;
                }

                this.selectedAudioId = audioId;
                this.$router.replace({path: this.$route.name, query: { pid: this.postData.postId, postType: this.postData.postType, aid: this.selectedAudioId}});
            } else {
                this.currentAudioElement = {
                    audioDataId: audioId,
                    audioData: audioData,
                };

                const pref = this.subtitleByAudioElementId[audioId];
                this.subtitlesByLanguageSelected = pref ? pref.subtitlesByLanguageSelected : false;
                if (this.subtitlesByLanguageSelected) {
                    this.selectedSubtitleLanguage = pref.language;
                }

                this.selectedAudioId = audioId;

                this.$refs.vidPlayer.removeMarkers();
                this.$refs.vidPlayer.playerSetTime(0.0);
                this.$router.replace({path: this.$route.name, query: { pid: this.postData.postId, postType: this.postData.postType, aid: this.selectedAudioId}});

                this.audioElementHasBegunPlaying = false;
            }
            this.loadAndPreloadFirstAudioData();
        },
        // markerReached and markerClicked were phased on in June/July of 2023 for a more efficient loading type of timeUpdate and videoPlayerSeeked, respectively.
        markerReached: function (/*marker, time*/) { // , index, time){
            return false;
        },
        markerClicked: function (/*marker*/){ // index, time){
            return false;
        },
        advancedControlsClick: function (advancedControl) {
            // console.log("clicked: ", advancedControl);
            // console.log("this.audioElementHasBegunPlaying: ", this.audioElementHasBegunPlaying);
            // console.log("audioElement: ", this.currentAudioElement.audioData);
            switch (advancedControl) {
                case 'previous': // restart video player (current audio element) from beginning
                    this.$refs.vidPlayer.playerSetTime(0.0);
                    return;
                case 'backward': // jump to previous audio segment
                    this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment != null ? (this.currentAudioSegment.index > 0 ? ((this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? (Math.abs(this.currentAudioElement.audioData.audio[this.currentAudioSegment.index - 1].audioStart + this.currentAudioElement.audioData.audio[this.currentAudioSegment.index - 1].audioDur) - 0.3) : (this.currentAudioElement.audioData.audio[this.currentAudioSegment.index - 1].audioStart - 0.1)) : ((this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? (Math.abs(this.currentAudioElement.audioData.audio[0].audioStart + this.currentAudioElement.audioData.audio[0].audioDur) - 0.3) : (this.currentAudioElement.audioData.audio[0].audioStart - 0.1))) : ((this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? (Math.abs(this.currentAudioElement.audioData.audio[this.currentAudioElement.audioData.audio.length - 1].audioStart + this.currentAudioElement.audioData.audio[this.currentAudioElement.audioData.audio.length - 1].audioDur) - 0.3) : (this.currentAudioElement.audioData.audio[this.currentAudioElement.audioData.audio.length - 1].audioStart - 0.1)));
                    return;
                case 'replay': // replay current, or jump to last played, audio segment
                    // this.replayAudioSegment();

                    !this.audioPlayer.paused /* audioPlayer is playing */
                        ? (this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? this.$refs.vidPlayer.playerSetTime(Math.abs(this.currentAudioSegment.audioStart+this.currentAudioSegment.audioDur)-0.3) : this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart-0.1) /* check to see if current audio segment should be set back to the start or end of the segment */
                        : (this.currentAudioSegment != null && this.currentAudioSegment.index == 0) ? null /* check to see if the first audio segment has been played and will prevent from causing an error */
                        : (this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? this.$refs.vidPlayer.playerSetTime(Math.abs(this.previousAudioSegment.audioStart+this.previousAudioSegment.audioDur)-0.3) : this.$refs.vidPlayer.playerSetTime(this.previousAudioSegment.audioStart-0.1); /* check to see if previous audio segment should be set back to the start or end of the segment */
                    return;
                case 'rewind': // rewind 10 seconds
                    var rewindTime = this.$refs.vidPlayer.playerGetTime() - 10;
                    if (rewindTime <= 0) rewindTime = 0;
                    this.$refs.vidPlayer.playerSetTime(rewindTime);
                    return;
                case 'play': // play video
                    this.$refs.vidPlayer.playerPlay();
                    return;
                case 'pause': // pause video
                    this.$refs.vidPlayer.playerPause();
                    return;
                case 'fast': // forward 10 seconds
                    var forwardTime = this.$refs.vidPlayer.playerGetTime() + 10;
                    if (forwardTime >= this.$refs.vidPlayer.playerGetDuration()) forwardTime = this.$refs.vidPlayer.playerGetDuration();
                    this.$refs.vidPlayer.playerSetTime(forwardTime-0.1);
                    return;
                case 'forward': // jump to next audio segment
                    (!this.audioPlayer.paused && this.nextAudioSegment != null) ? this.$refs.vidPlayer.playerSetTime(this.nextAudioSegment.audioStart) : (this.currentAudioSegment != null && this.currentAudioSegment.index < this.currentAudioElement.audioData.audio.length - 1) ? this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart) : this.currentAudioSegment == null ? null : this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart + this.currentAudioSegment.audioDur);
                    this.skipToNextAudioSegment();
                    // console.log("play next audio segment: ", this.nextAudioSegment);
                    // this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment != null ? (this.currentAudioSegment.index < this.currentAudioElement.audioData.audio.length - 1 ? ((this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? (Math.abs(this.currentAudioElement.audioData.audio[this.currentAudioSegment.index + 1].audioStart + this.currentAudioElement.audioData.audio[this.currentAudioSegment.index + 1].audioDur) - 0.3) : (this.currentAudioElement.audioData.audio[this.currentAudioSegment.index + 1].audioStart - 0.1)) : ((this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? ((this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? (Math.abs(this.currentAudioElement.audioData.audio[this.currentAudioElement.audioData.audio.length - 1].audioStart + this.currentAudioElement.audioData.audio[this.currentAudioElement.audioData.audio.length - 1].audioDur) - 0.3) : (this.currentAudioElement.audioData.audio[this.currentAudioElement.audioData.audio.length - 1].audioStart - 0.1)) : null)) : null);
                    // (!this.audioPlayer.paused && this.nextAudioSegment != null) /* audioPlayer is playing and not the last segment */
                    //     ? (this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? this.$refs.vidPlayer.playerSetTime(Math.abs(this.nextAudioSegment.audioStart+this.nextAudioSegment.audioDur)-0.3) : this.$refs.vidPlayer.playerSetTime(this.nextAudioSegment.audioStart-0.1) /* check to see if next audio segment should be set to the start or end of the segment */
                    //     : (this.currentAudioSegment != null && this.currentAudioSegment.index < this.currentAudioElement.audioData.audio.length - 1) ? ((this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? this.$refs.vidPlayer.playerSetTime(Math.abs(this.currentAudioSegment.audioStart+this.currentAudioSegment.audioDur)-0.3) : this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart-0.1)) /* check to see if a we need to skip to first segment */
                    //     : null; /* if there are not more segments to skip to */
                    return;
                case 'skip': // skip to next audio segment
                    (!this.audioPlayer.paused && this.nextAudioSegment != null) ? this.$refs.vidPlayer.playerSetTime(this.nextAudioSegment.audioStart) : (this.currentAudioSegment != null && this.currentAudioSegment.index < this.currentAudioElement.audioData.audio.length - 1) ? this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart) : this.currentAudioSegment == null ? null : this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart + this.currentAudioSegment.audioDur);
                    this.skipToNextAudioSegment();
                    // console.log("skip to end of current audio segment");
                    return;
                case 'next': // play next audio element
                    // var nextAudioElementIndex = Math.min(this.currentAudioElementIndex + 1, this.audioData.length - 1);
                    // if (nextAudioElementIndex == this.currentAudioElementIndex) return;
                    // this.currentAudioElementIndex = nextAudioElementIndex;
                    // this.changeAudioElement(this.audioData[nextAudioElementIndex].audioData, this.audioData[nextAudioElementIndex].audioDataId);
                    // this.currentAudioSegment.index < this.currentAudioElement.audioData.audio.length - 1 ? this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart + this.currentAudioSegment.audioDur) : null;
                    this.$refs.vidPlayer.playerSetTime((this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? (this.currentAudioElement.audioData.audio[this.currentAudioElement.audioData.audio.length - 1].audioStart + (this.currentAudioElement.audioData.audio[this.currentAudioElement.audioData.audio.length - 1].audioDur)*2) : ((this.currentAudioElement.audioData.audio[this.currentAudioElement.audioData.audio.length - 1].audioStart + this.currentAudioElement.audioData.audio[this.currentAudioElement.audioData.audio.length - 1].audioDur)));
                    return;
                case 'captions':
                    this.showCaptions = !this.showCaptions;
                    // this.showCaptions ^= true;
                    return;
                case 'playbackStyle':
                    this.advancedControlsOptionsClicked = true;
                    this.advancedControl = advancedControl;
                    return;
                case 'volume':
                    this.advancedControlsOptionsClicked = true;
                    this.advancedControl = advancedControl;
                    return;
                case 'speed':
                    this.advancedControlsOptionsClicked = true;
                    this.advancedControl = advancedControl;
                    return;
                case 'languages':
                    // this.advancedControlsOptionsClicked = true;
                    this.selectedSubtitleLanguage == null && (this.selectedSubtitleLanguage = this.currentAudioElement.audioData.languageName);
                    // console.log("languages clicked: ", this.selectedSubtitleLanguage);
                    // console.log("languages clicked: ", this.vMenuLanguages());

                default:
                    return;
            }
        },
        setSelectedSubtitleLanguage: function(language) {
            this.subtitlesByLanguageSelected = true;
            this.selectedSubtitleLanguage = language;//.split('-')[0].trim();
            if (this.selectedSubtitleLanguage === this.currentAudioElement.audioData.languageName) this.subtitlesByLanguageSelected = false;
            this.showLanguageSelectionMenu = false;
            this.updateSelectedSubtitleLanguage(this.selectedSubtitleLanguage, this.subtitlesByLanguageSelected, this.currentAudioElement);
        },
        playbackStyleChanged: function () {
            // When leaving continuous mode, restore video to full volume.
            // When entering continuous mode, apply the crossfade.
            if (this.playbackStyle === 'continuous') {
                this.$refs.vidPlayer.playerSetVolume(1.0 - this.captionVolume);
            } else {
                this.$refs.vidPlayer.playerSetVolume(1.0);
            }
            this.$refs.vidPlayer.removeMarkers();
            this.$refs.vidPlayer.getMarkers().length <= 0 && this.$refs.vidPlayer.placeMarkers(this.currentAudioElement.audioData.audio, this.currentAudioElement.audioData.subtitles, this.currentAudioElement.audioData.username)
            this.delayAdvancedOptionsClick();
        },
        setPlaybackStyle: function (value) {
            // Independent buttons (matching the overlap row) replaced the v-radio-group so the
            // selected label picks up the highlight — a v-radio only colors its icon, never its
            // label. Guard mirrors setOverlapMode: re-tapping the active style is a no-op.
            if (this.playbackStyle === value) return;
            this.playbackStyle = value;
            this.playbackStyleChanged();
        },
        setOverlapMode: function (value) {
            // Independent spaced buttons (mirroring the Continuous / Pause-Play row above for easier
            // tapping) instead of a joined v-btn-toggle; this guard replaces the toggle's own
            // no-op-on-same-value behavior so re-tapping the active mode doesn't re-queue.
            if (this.playbackStyleSameStart === value) return;
            this.playbackStyleSameStart = value;
            this.overlapModeChanged();
        },
        overlapModeChanged: async function () {
            // Re-queue the upcoming segment under the newly chosen overlap mode. Without this the
            // previously queued segment (computed under the old mode) would keep playing or skipping
            // the old way until the next natural promotion or a seek rebuilt the queue.
            if (!this.currentAudioSegment || !this.currentAudioElement) return;
            const audio = this.currentAudioElement.audioData.audio;
            const hasMoreSegments = (audio.length - 1) >= (this.currentAudioSegment.index + 1);
            if (!hasMoreSegments) return this.nextAudioSegment = null;

            const nextIndex = (this.playbackStyle === 'continuous' && this.playbackStyleSameStart)
                ? await this.getNextAvailableIndexAfterOverlap(this.currentAudioSegment.index, this.currentAudioSegment.index + 1)
                : this.currentAudioSegment.index + 1;

            if (nextIndex === null) { // All remaining AudioSegments overlap, nothing to queue.
                this.nextAudioSegment = null;
                this.nextAudioPlayer = null;
                return;
            }

            this.nextAudioSegment = this.applySegmentMetadata(audio[nextIndex], nextIndex);
            this.nextAudioPlayer = new Audio();
            this.nextAudioPlayer.volume = this.captionVolume; // preserve caption volume on the preloaded player
            this.nextAudioPlayer.preload = 'auto';
            this.nextAudioPlayer.src = this.nextAudioSegment.audioURL;
            this.nextAudioPlayer.load();
        },
        // rewindOrForwardClicked: function (time) {
        //     console.log("time: ", time);
        //     this.$refs.vidPlayer.playerSetTime(time);
        //     // Do I need to check to see if an audio segment has been reached here???
        //     this.$refs.vidPlayer.playerPlay(); // on occasion, video player will play and immediately pause. I think this has something to do with hitting an audio segment
        //     // console.log("rewindOrForwardClicked end");
        //     // this.rewindForwardTimer = setTimeout(() => { console.log(""); }, 100); // this can be used to add up time to show. For example, if double clicked then time skip forward would show 20 sec
        // },
        closeAdvancedOptionsClicked: function () {
            clearTimeout(this.delayAdvancedOptionsClickTimer);
            this.advancedControlsOptionsClicked = false
        },
        delayAdvancedOptionsClick: function () {
            this.delayAdvancedOptionsClickTimer = setTimeout(() => { this.advancedControlsOptionsClicked = false }, 250);
        },
        replayAudioSegment: function () {
            // console.log("replayAudioSegment: ", !this.audioPlayer.paused);

            !this.audioPlayer.paused /* audioPlayer is playing */
                ? (this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? this.$refs.vidPlayer.playerSetTime(Math.abs(this.currentAudioSegment.audioStart+this.currentAudioSegment.audioDur)-0.3) : this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart) /* check to see if current audio segment should be set back to the start or end of the segment */
                : (this.currentAudioSegment != null && this.currentAudioSegment.index == 0) ? null /* check to see if the first audio segment has been played and will prevent from causing an error */
                : (this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? this.$refs.vidPlayer.playerSetTime(Math.abs(this.previousAudioSegment.audioStart+this.previousAudioSegment.audioDur)-0.3) : this.$refs.vidPlayer.playerSetTime(this.previousAudioSegment.audioStart); /* check to see if previous audio segment should be set back to the start or end of the segment */

            // console.log("replayAudioSegment");
            // console.log("this.currentAudioElement: ", this.currentAudioElement);
            // console.log("");

            // console.log("this.previousAudioSegment: ", this.previousAudioSegment);
            // console.log("this.currentAudioSegment: ", this.currentAudioSegment);
            // console.log("this.nextAudioSegment: ", this.nextAudioSegment);
            // console.log("");

            // !this.audioPlayer.paused /* audioPlayer is playing */ ? this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart) : (this.currentAudioSegment != null && this.currentAudioSegment.index == 0) ? null : this.$refs.vidPlayer.playerSetTime(this.previousAudioSegment.audioStart);

            // (this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart+this.currentAudioSegment.audioDur) : this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart);

            // if (!this.audioPlayer.paused) { // if audio player is currently playing, then restart the current audio segment
            //     // console.log("RESTART CURRENTLY PLAYING SEGMENT");
            //     this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart - 0.1);
            // } /*else if (this.currentAudioSegment != null && this.currentAudioSegment.index == 0) {
            //     // play the current audio segment
            //     // otherwise if the index of the current segment is 0, play the segment
            //     // console.log("FIRST SEGMENT HAS NOT BEEN PLAYED YET");
            // }*/ else { // else play the segment at index - 1, previousAudioSegment
            //     // console.log("REPLAY PREVIOUS SEGMENT");
            //     // console.log("this.previousAudioSegment: ", this.previousAudioSegment);
            //     this.$refs.vidPlayer.playerSetTime(this.previousAudioSegment.audioStart - 0.1);
            //     // this.currentAudioSegment = this.previousAudioSegment;
            // }
        },
        skipToNextAudioSegment: function () {
            // console.log("skipToNextAudioSegment");
            // console.log("this.currentAudioElement: ", this.currentAudioElement);
            // console.log("this.currentAudioSegment: ", this.currentAudioSegment);

            // (!this.audioPlayer.paused && this.nextAudioSegment != null) ? this.$refs.vidPlayer.playerSetTime(this.nextAudioSegment.audioStart) : (this.currentAudioSegment != null && this.currentAudioSegment.index < this.currentAudioElement.audioData.audio.length - 1) ? this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart) : this.currentAudioSegment == null ? null : this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart + this.currentAudioSegment.audioDur);

             (!this.audioPlayer.paused && this.nextAudioSegment != null) /* audioPlayer is playing and not the last segment */
                ? (this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? this.$refs.vidPlayer.playerSetTime(Math.abs(this.nextAudioSegment.audioStart+this.nextAudioSegment.audioDur)-0.3) : this.$refs.vidPlayer.playerSetTime(this.nextAudioSegment.audioStart) /* check to see if next audio segment should be set to the start or end of the segment */
                : (this.currentAudioSegment != null && this.currentAudioSegment.index < this.currentAudioElement.audioData.audio.length - 1) ? ((this.playbackStyle == 'pauseplay' && !this.playbackStyleAudioFirst) ? this.$refs.vidPlayer.playerSetTime(Math.abs(this.currentAudioSegment.audioStart+this.currentAudioSegment.audioDur)-0.3) : this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart)) /* check to see if a we need to skip to first segment */
                : null; /* if there are not more segments to skip to */

            // if (!this.audioPlayer.paused && this.nextAudioSegment != null) {
            //     // if audio player is currently playing, then restart the current audio segment
            //     // console.log("SKIP TO NEXT SEGMENT");
            //     this.$refs.vidPlayer.playerSetTime(this.nextAudioSegment.audioStart - 0.1);
            // } else if (this.currentAudioSegment != null && this.currentAudioSegment.index < this.currentAudioElement.audioData.audio.length - 1) { // greater than length of currentAudioElement.segments - 1
            //     // play the current audio segment
            //     // otherwise if the index of the current segment is 0, play the segment
            //     // console.log("SKIP TO CURRENT SEGMENT");
            //     this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart - 0.1);
            // } else {
            //     // else play the segment at index - 1
            //     // console.log("???");
            //     this.$refs.vidPlayer.playerSetTime(this.currentAudioSegment.audioStart + this.currentAudioSegment.audioDur);
            // }
        },
        getPostData: function () {
            firebase.db.collection("publicPosts").doc(this.postId).get().then((doc) => {
                if (!doc.exists) {
                    this.viewLoading = false;
                    this.$store.commit('alertUser', { show: true, text: `This post does not exist.`, type: 'snackbar' });
                    return;
                }
                // var date = doc.data().postDate.toDate().toLocaleDateString(); // should put date in format: MM/DD/YYYY;
                // var date = doc.data().postDate.toDate().toDateString().slice(4); // should put the date in the format: MMM DD YYYY;
                let day = doc.data().postDate.toDate().getDate();
                let month = doc.data().postDate.toDate().toLocaleDateString('default', {month: 'short'});
                let year = doc.data().postDate.toDate().getFullYear();
                let formattedDate = `${day} ${month} ${year}`;
                this.postData = doc.data();
                this.postData.postDate = formattedDate; // should put the date in the format: DD MMM YYYY;
                // this.postData.postDate = date; // uncomment to use one of the date formats commented out above;

                // the following code block will check to see if a post has 'multipleLanguagesTitle' object in the database, and if so, will set the title and audioDataId to the corresponding default browser language or to default (english)
                let userDefaultBrowserLanguage = (navigator.userLanguage || navigator.language).split('-')[0];
                if (typeof this.postData.multipleLanguagesTitle === 'object' && this.postData.multipleLanguagesTitle !== null && this.postData.multipleLanguagesTitle.hasOwnProperty(userDefaultBrowserLanguage)) {
                    this.postData.title = this.postData.multipleLanguagesTitle[userDefaultBrowserLanguage].title;
                    this.postData.postAudioDataId = this.postData.multipleLanguagesTitle[userDefaultBrowserLanguage].audioId;
                }

                this.descriptionWordCount = this.wordCount();
                this.$store.dispatch('saveCurrentPost', this.postData);
                var newSrc = {
                    type: this.postData.videoType,
                    src: this.postData.videoUrl,
                };
                this.vidOptions.sources= newSrc;
                this.letUserSeePost = true;
                this.viewLoading = false;
                if(this.postMetadataHasChanged) {
                    this.$store.commit("updatePostData",this.postData); // this updates the postData in the store so that the homepage, and other pages, can get the changes aswell.
                    this.postMetadataHasChanged = false;
                    this.editPostMetadataLoading = false;
                }
                if (this.editPost) this.editPost = false;
            }).catch((err) => {
                this.$store.commit('alertUser', { show: true, text: `Something went wrong. ${err}`, type: 'snackbar' });
                this.viewLoading = false;
            });
        },
        getGroupPostData: function () {
            firebase.db.collection("groupPosts").doc(this.postId).get().then((doc) => {
                if (!doc.exists) {
                    this.$store.commit('alertUser', { show: true, text: `This post does not exist.`, type: 'snackbar' });
                    this.viewLoading = false;
                    return;
                }
                this.postData = doc.data();
                if (this.isUserPartOfGroupFunc(this.userGroups) || this.userData.admin) {
                    let day = doc.data().postDate.toDate().getDate();
                    let month = doc.data().postDate.toDate().toLocaleDateString('default', {month: 'short'});
                    let year = doc.data().postDate.toDate().getFullYear();
                    let formattedDate = `${day} ${month} ${year}`;
                    this.postData.postDate = formattedDate; // should put the date in the format: DD MMM YYYY;
                    this.descriptionWordCount = this.wordCount();
                    this.$store.dispatch('saveCurrentPost', this.postData);
                    var newSrc = {
                        type: this.postData.videoType,
                        src: this.postData.videoUrl,
                    };
                    this.vidOptions.sources = newSrc;
                    this.letUserSeePost = true;
                    this.viewLoading = false;
                    if (this.postMetadataHasChanged) {
                        this.$store.commit("updatePostData",this.postData); // this updates the postData in the store so that the homepage, and other pages, can get the changes aswell.
                        this.postMetadataHasChanged = false;
                        this.editPostMetadataLoading = false;
                    }
                    if (this.editPost) this.editPost = false;
                } else {
                    this.$store.commit('alertUser', { show: true, text: `This post can only be seen members of the group it belongs to.`, type: 'snackbar' });
                    this.viewLoading = false;
                }
            }).catch((err) => {
                this.viewLoading = false;
                this.$store.commit('alertUser', { show: true, text: `Something went wrong. ${err}`, type: 'snackbar' });
            });
        },
        adjustSegmentVolume: function (value) {
            this.captionVolume = value;
            if (this.captionVolume > 0.0) this.lastCaptionVolume = this.captionVolume; // remember for unmute restore, however the volume was set
            if (this.playbackStyle == 'continuous') this.$refs.vidPlayer.playerSetVolume(1.0 - this.captionVolume);
            this.audioPlayer.volume = this.captionVolume;
        },
        toggleCaptionAudioMute: function () {
            // Explicit mute/unmute for the slider's speaker icon (replaces the old inline
            // 'mute' branch, which unmuted by jumping to 1.0 — in continuous mode that ducks
            // the VIDEO to silence, so between caption segments the unmute was inaudible and
            // felt like it did nothing). Unmute restores the last audible volume instead.
            this.adjustSegmentVolume(this.captionVolume > 0.0 ? 0.0 : (this.lastCaptionVolume > 0.0 ? this.lastCaptionVolume : 1.0));
        },
        visibilityChanged: function () {
            if (document.hidden) {
                this.$refs.vidPlayer.playerPause();
                this.audioPlayer.pause();
            }
        },
        setAudioPermissions: function () {
            // permissions: 0 = none, 1 = all,  2 = only current users and main one
            return new Promise((resolve) => {
                if (this.postData?.postType === 'group') { // GROUP POST
                    // console.log("setAudioPermissions - this.userGroups: ", this.userGroups);
                    const index = this.userGroups?.findIndex((group) => group.groupId === this.postData?.groupId);
                    // console.log("setAudioPermissions - this.userGroups - index: ", index);
                    if ( index != -1 ) { // index exists in userGroups
                        this.isGroupAdmin = this.userGroups[index]?.admin || false;
                        if (this.isGroupAdmin) { // User is group admin.
                            resolve(1);
                        } else { // User is NOT admin.
                            if (this.userGroups[index]?.privateContributions) {
                                if (this.userData?.admin) resolve(1); // user is tawq.in ADMIN
                                else resolve(2); // private contributions are set on the group, and the user is not an admin.
                            } else {
                                resolve(1); // the user is not an admin and private contributions are not set
                            }
                        }
                    } else { // index does NOT exist in userGroups
                        if (this.userData?.admin) resolve(1); // tawq.in admin can see any post.
                        else resolve(0);
                    }
                    return;
                }
                // PUBLIC POST, ALLOW ALL
                resolve(1);
            });
            // if (this.postData.postType === 'group') {
            //     const index = this.userGroups.findIndex((group) => group.groupId === this.postData.groupId);
            //     if ( index != -1 ) { // index exists in userGroups
            //         this.isGroupAdmin = this.userGroups[index].admin || false;
            //         if (this.isGroupAdmin) { // User is group admin.
            //             return 1;
            //         } else { // User is NOT admin.
            //             if (this.userGroups[index].privateContributions) {
            //                 if (this.userData.admin) return 1; // user is tawq.in ADMIN
            //                 return 2; // private contributions are set on the group, and the user is not an admin.
            //             } else {
            //                 return 1; // the user is not an admin and private contributions are not set
            //             }
            //         }
            //     } else { // index does NOT exist in userGroups
            //         if (this.userData.admin) return 1; // tawq.in admin can see any post.
            //         return 0;
            //     }
            // }
            // // Public post, allow all.
            // return 1;
        },
        getSpecificAudioData: async function (specificAudioDataId) {
            const doc = await firebase.db.collection("audioSegments").doc(specificAudioDataId).get();
            if (!doc.exists) return;

            const data = {
                audioData: doc.data(),
                audioDataId: doc.id,
            };

            const index = this.audioData.findIndex(audio => audio.audioDataId === specificAudioDataId);
            if (index !== -1) {
                this.audioData.splice(index, 1, data);
            } else {
                // if it wasn't in the list for some reason, add it
                this.audioData.push(data); // or unshift(data)
            }
        },
        getMainPostAudioData: async function () {
            // this method retrieves the postAudioData (the main audio attached to the post) only. currently being used after editing a post... this way we aren't retrieving ALL of them when only one has been edited...
            let permissions = await this.setAudioPermissions();
            if (permissions === 0) {
                return;
            }
            const audioRef = firebase.db.collection('audioSegments').doc(this.postData.postAudioDataId);
            const audioDoc = await audioRef.get().catch((err) => {
                console.log("error retrieving the postAudioData document. ", err.message);
            })
            const data = {
                audioData: audioDoc.data(),
                audioDataId: audioDoc.id,
            };
            const index = this.audioData.findIndex( audio => audio.audioDataId === this.currentPost.postAudioDataId);
            this.audioData.splice(index != -1 ? index : 0, 1, data);
            this.audioLoading = false;
        },
        getAudioData: async function () {
            // console.log("1. getAudioData called");
            /* This function will retrieve the metadata for the audio segments, not the audio itself.
            We will query the owners audio segments first, adding them to our local array. after we will
            query every audio segment, and only add it to the array if it hasn't been added yet. Firebase Querying
            does not allow '!=', or for us to use where clause with orderby clause on different fields. */

            // July 13, 2023: the following incrementPostViewed if statement was moved here in hopes that it will get called everytime and not just in the $nextTick function of setPost();
            this.postViewedCounter = setTimeout(() => {
                this.incrementPostViewed(); // this only increments when on production. I.E. tawq.in NOT localhost or developer.tawq.in;
            }, 30000); // increment the posts viewed by one after 30 seconds.

            this.$refs.vidPlayer.player.on('volumechange', () => {
                var p = this.$refs.vidPlayer.player;
                this.videoIsMuted = p.muted() || p.volume() === 0;
                this.videoVolume = (p.muted() || p.volume() === 0) ? 0 : p.volume();
            });

            // Apply sender's video volume if share link included &vv=
            if (this.senderSettingsActive && this.videoVolume !== 1.0) {
                this.$refs.vidPlayer.player.volume(this.videoVolume);
            }

            let permissions = await this.setAudioPermissions();
            // console.log("audio permissions: ", permissions);
            if (permissions === 0) { return; }
            this.audioData = [];
            await firebase.db.collection("audioSegments").where("postId", "==", this.postData.postId).orderBy("date", "desc").get().then((snapshot) => {
                snapshot.forEach((doc) => {
                    let data = {
                        audioData: doc.data(),
                        audioDataId: doc.id,
                    };
                    /* first check to see that there is audio here, then if it is the main audioData that is associated with the post, add it to the front not the back */
                    if (data.audioData.audio.length > 0) {
                        if (data.audioDataId === this.postData.postAudioDataId) {
                            this.audioData.unshift(data);
                        } else {
                            // permissions: 0 = none, 1 = all,  2 = only current users and main one
                            if (permissions === 1) {
                                this.audioData.push(data);
                            }
                            else if (permissions === 2) {
                                if (doc.data().userId === firebase.auth.currentUser.uid){
                                    this.audioData.push(data);
                                } else {
                                    return;
                                }
                            }
                        }
                    }
                    if (typeof data.audioData.languageName != 'undefined' && !this.filter.languageCodes.includes(data.audioData.languageName)){
                        this.filter.languageCodes.push(data.audioData.languageName)
                    }
                });
                if (!this.$route.query.aid) {
                    this.selectedAudioId = this.audioData[0].audioDataId; // no aid in url, set first AudioElement to be highlighted.
                    this.currentAudioElementIndex = 0;
                    this.$router.replace({path: this.$route.name, query: { pid: this.postData.postId, postType: this.postData.postType, aid: this.selectedAudioId}});
                } else {
                    this.selectedAudioId = this.$route.query.aid; // set this so the AudioElement that will play when clicking video or big play button is highlighted.
                }
                // this.currentAudioElement = this.audioData.find(data => data.audioDataId === this.selectedAudioId);
                this.currentAudioElementIndex = this.audioData.findIndex(data => data.audioDataId === this.selectedAudioId);
                this.currentAudioElement = this.currentAudioElementIndex !== -1 ? this.audioData[this.currentAudioElementIndex] : null;
                this.loadAndPreloadFirstAudioData();
                this.audioLoading = false;
                // this.videoLoading = false; // July 27, 2023. This shouldn't be necessary, but is being kept commented out in case of error or issue that requires it uncommented.
            }).catch((err) => {
                this.audioLoading = false;
                this.videoLoading = false;
                console.error(err.message);
                this.$store.commit('alertUser', { show: true, text: `Something went wrong. ${err.message}`, type: 'snackbar' });
            });
        },
        wordCount: function () {
            if (!this.postData || !this.postData.description) return 0;
            var temp = this.postData.description;
            temp = temp.replace(/(^\s*)|(\s*$)/gi,"");
            temp = temp.replace(/[ ]{2,}/gi," ");
            temp = temp.replace(/\n /,"\n");
            return temp.split(' ').length;
        },
        incrementPostViewed: function() {
            if (process.env.NODE_ENV === 'production') {
                let postRef;
                if (typeof this.postData.postId != 'undefined') {
                    postRef = firebase.db.collection(this.postData.postType === 'public' ? "publicPosts" : "groupPosts").doc(this.postData.postId);
                } else if (typeof this.postId != 'undefined') {
                    postRef = firebase.db.collection(this.postType === 'public' ? "publicPosts" : "groupPosts").doc(this.postId);
                } else {
                    return;
                }
                postRef.update({postViewed: fb.firestore.FieldValue.increment(1),}).catch((error) => {
                    console.error("error incrementing the postViewed:", error.message);
                })
            } else {
                return;
            }
        },
        isUserPartOfGroupFunc: function(groups) {
            if (!groups.length) return false;
            return groups.some(group => group.groupId === this.postData.groupId);
        },
        setPost (timesCalled) {
            // setPost() looks to see if we can use the current post from the store. if we can, then we don't have to fetch it from firebase. If we try through 5 ticks, with no success, then we will retrieve the post from firebase.
            // we have to wrap the code in the processNextTick() function because when viewPost is created, the store variable hasn't been set
            if (timesCalled > 5) { // base case: if we have tried this 5 times already, we fallback to getting the post info from firebase;
                this.postType === 'group' ? this.getGroupPostData() : this.getPostData();
                return;
            } else {
                this.$nextTick(() => {
                    if (this.currentPost.postId === this.postId) {
                        this.postData = { ...this.currentPost };
                        if (this.postType === 'group' && !this.isUserPartOfGroupFunc(this.userGroups) && !this.userData.admin) {
                            this.$store.commit('alertUser', { show: true, text: `This post can only be seen members of the group it belongs to.`, type: 'snackbar' });
                            this.viewLoading = false;
                            return;
                        }
                        this.descriptionWordCount = this.wordCount(this.postData.description);
                        this.vidOptions.sources = {
                            type: this.postData.videoType,
                            src: this.postData.videoUrl,
                        };
                        this.letUserSeePost = true;
                        this.viewLoading = false;
                    } else {
                        this.setPost(timesCalled + 1); // recursively call this function to see if the store has updated
                    }
                });
            }
        },
        resetToMySettings: function () {
            // Restore the user's baseline (stored preferences falling through to CANONICAL_DEFAULTS),
            // via the same baselineSettings computed the status bar compares against.
            const base = this.baselineSettings;
            this.playbackStyle = base.playbackStyle;
            this.showCaptions = base.showCaptions;
            this.captionVolume = base.captionVolume;
            this.audioPlayer.volume = this.captionVolume;
            this.playbackRate = base.playbackRate; // watcher propagates to video player and audioPlayer
            if (this.playbackStyleSameStart !== base.playbackStyleSameStart) {
                this.playbackStyleSameStart = base.playbackStyleSameStart;
                this.overlapModeChanged(); // re-queue the upcoming segment under the restored mode
            }
            if (this.$refs.vidPlayer && this.$refs.vidPlayer.player) {
                // muted() is a separate state from volume() in VideoJS — setting volume alone does not unmute.
                this.$refs.vidPlayer.player.muted(false);
                this.$refs.vidPlayer.player.volume(this.expectedVideoVolume); // 1.0 in pauseplay; 1.0 - captionVolume in continuous
            }
            this.videoVolume = this.expectedVideoVolume;
            this.senderSettingsActive = false;
            this.statusBarDismissedKey = null; // bar disappears because anomalies are gone, not because it was dismissed
            // Clean the share params out of the URL without triggering a navigation. Guarded because
            // this method is now also reachable from the status bar with no share params present,
            // and vue-router 3 throws NavigationDuplicated when replacing with an identical route.
            if (this.$route.query.share !== undefined) {
                this.$router.replace({ query: { pid: this.postData.postId, postType: this.postData.postType, aid: this.selectedAudioId } });
            }
        },
        dismissStatusBar: function () {
            // Session-only. The overlap hint is educational and once-per-session: if it was
            // visible when the X was tapped, it counts as seen and won't re-trigger the bar.
            // Then store the identity of the REMAINING anomaly set (computed after the hint
            // is cleared); if the set changes later the key no longer matches and the bar
            // reappears on its own. Nothing persists across page loads.
            if (this.showOverlapHint) this.dismissedOverlapHint = true;
            if (this.showDeadAirHint) this.dismissedDeadAirHint = true;
            this.statusBarDismissedKey = this.settingsAnomaliesKey;
        },
        openAdvancedControlFromStatusBar: function (anomaly) {
            // The status bar is read-only: a tap opens Advanced Controls on the relevant setting.
            // Deliberately does NOT route through advancedControlsClick — its 'captions' case
            // TOGGLES the setting rather than opening a panel, and a status tap must never
            // silently change state.
            this.viewAdvancedControls = true;
            if (anomaly.control === 'captions') {
                this.advancedControlsOptionsClicked = false; // captions is a toggle button on the main panel; just open the dialog
                return;
            }
            this.advancedControlsOptionsClicked = true;
            this.advancedControl = anomaly.control;
        },
        muteVideo: function () {
            this.$refs.vidPlayer.player.volume(0);
            this.dismissedOverlapHint = true;
        },
        unmuteCaptionAudio: function () {
            // One-click fix for the dead-air hint. Restores the last audible caption volume
            // (same restore rule as toggleCaptionAudioMute). Marks the hint seen so it doesn't
            // reappear if the user deliberately mutes again later this session.
            this.adjustSegmentVolume(this.lastCaptionVolume > 0.0 ? this.lastCaptionVolume : 1.0);
            this.dismissedDeadAirHint = true;
        },
    },
    watch: {
        playbackRate: function (rate) {
            this.$refs.vidPlayer.playerSetPlaybackRate(rate);
            this.audioPlayer.playbackRate = rate;
        },
        playbackStyle: function (newVal) {
            if (!this.$refs.vidPlayer) return;
            if (newVal === 'continuous') {
                this.$refs.vidPlayer.playerSetVolume(1.0 - this.captionVolume);
            } else {
                this.$refs.vidPlayer.playerSetVolume(1.0);
            }
            this.$nextTick(() => {
                if (!this.currentAudioElement || this.$refs.vidPlayer.getMarkers().length <= 0) return;
                this.$refs.vidPlayer.removeMarkers();
                this.$refs.vidPlayer.placeMarkers(this.currentAudioElement.audioData.audio, this.currentAudioElement.audioData.subtitles, this.currentAudioElement.audioData.username);
                if (this.searchQuery && this.searchQuery.trim()) this.updateSearchMarkers(this.searchQuery); // removeAll wiped search markers too
            });
        },
        searchQuery: function(newVal) {
            this.updateSearchMarkers(newVal);
            // If search is cleared, collapse the color picker so it's fresh next time
            if (!newVal || !newVal.trim()) this.showColorPicker = false;
        },
        searchHighlightColor: function() {
            // Re-render search markers in new color. updateSearchMarkers always clears
            // previous search markers first, so no explicit cleanup needed here.
            if (this.searchQuery && this.searchQuery.trim()) {
                this.updateSearchMarkers(this.searchQuery);
            }
        },
        playingSegmentIndex: function(newIdx, oldIdx) {
            // Fires whenever the active segment changes, including when
            // currentAudioSegment is reassigned to a new object (e.g. advancing
            // to nextAudioSegment on playback).
            if (newIdx === oldIdx) return;
            this.scrollPlayingSegmentIntoView();
        },
    },
    computed: {
        playingSegmentIndex() {
            // Single source of truth for "which segment is currently playing".
            // Depending on currentAudioSegment directly means Vue re-evaluates this
            // whenever that property is reassigned to a new object (advancing
            // segments, seeking, clicking a different segment).
            if (!this.currentAudioSegment || typeof this.currentAudioSegment.index !== 'number') return -1;
            return this.currentAudioSegment.index;
        },
        isMobile(){
            return this.$store.state.isMobileDevice;
        },
        isIOS(){
            return this.$store.state.isIOSDevice;
        },
        getMaxHeightMainContainer() {
            if (this.$vuetify.breakpoint.smAndDown) {
                return '55vh'; // Max height for small and down screens (sm/xs)
            } else {
                return '80vh'; // Max height for medium and up screen (md/lg/xl)
            }
        },
        getMaxHeightSubContainer() {
            if (this.$vuetify.breakpoint.smAndDown) {
                return '50vh'; // Max height for small and down screens (sm/xs)
            } else {
                return '60vh'; // Max height for medium and up screen (md/lg/xl)
            }
        },
        matchedAudioData(){
            // Start from the language-filtered list so search composes with the existing filter
            const base = this.filter.languageSelected === "All"
                ? this.audioData
                : this.audioData.filter(audio => audio.audioData.languageName === this.filter.languageSelected);

            const q = (this.searchQuery || "").trim().toLowerCase();
            if (!q) return base;

            // Match against the primary subtitles AND every auto-translated subtitle bucket.
            // A contribution surfaces if ANY subtitle (in any language) contains the query.
            const subtitleArrayMatches = (subs) => {
                if (!Array.isArray(subs)) return false;
                return subs.some(s => s && s.subtitle && s.subtitle.toLowerCase().includes(q));
            };

            return base.filter(audio => {
                if (subtitleArrayMatches(audio.audioData.subtitles)) return true;
                const byLang = audio.audioData.subtitlesByLanguage;
                if (byLang && typeof byLang === 'object') {
                    return Object.values(byLang).some(subtitleArrayMatches);
                }
                return false;
            });
        },
        languageFilteredAudio(){
            return this.matchedAudioData;
        },
        // items(){
        //     if(this.filter.languageSelected === "All"){
        //         console.log("AudioData: ", this.audioData);
        //         return this.audioData;
        //     } else {
        //         return this.audioData.filter( audio => audio.audioData.languageName === this.filter.languageSelected );
        //     }
        // },
        leftAudioData(){
            return this.matchedAudioData.filter((_, index) => index % 2 === 0);
        },
        rightAudioData(){
            return this.matchedAudioData.filter((_, index) => index % 2 !== 0);
        },
        userData(){
            return this.$store.state.userData;
        },
        userGroups(){
            const groups = this.$store.state.userGroups;
            // console.log("userGroups in computed: ", groups);
            if (!groups) {
                // console.log("no user groups found");
                return [];
            } else {
                return groups;
            }
            // return this.$store.state.userGroups;
        },
        userGroupLength(){
            return this.$store.getters.getUserGroupsLength
        },
        currentPost(){
            return this.$store.state.currentPost;
        },
        displayLang() {
            const langMap = {
                en: 'English',
                es: 'Spanish',
                ar: 'Arabic',
                // Add more language mappings as needed
            };

            return langMap[this.currentAudioElement.audioData.languageCode.slice(0,2)] || this.currentAudioElement.audioData.languageCode.slice(0,2);
        },
        vMenuLanguages() {
            const audioData = this.currentAudioElement?.audioData;
            if (!audioData) return [];

            const originalLangName = (audioData.languageName || "");

            // Start with translated language codes
            const translatedLangCodes = Object.keys(audioData.subtitlesByLanguage || {});

            // Build menu options = translated + original (if original exists)
            const langs = [
                ...translatedLangCodes,
                ...(originalLangName ? [originalLangName] : []),
            ];

            // Remove duplicates (in case original is also in translated keys somehow)
            const uniqueLangs = Array.from(new Set(langs));

            // Remove the currently selected language (works for original or translated)
            return uniqueLangs.filter(lang => lang !== this.selectedSubtitleLanguage);
        },
        showOverlapHint() {
            return this.playbackStyle === 'continuous'
                && this.audioElementHasBegunPlaying // parity with PostView — not before playback starts
                && this.showCaptions
                && this.captionVolume > 0
                && !this.videoIsMuted
                && !this.dismissedOverlapHint;
        },
        // Dead-air hint: in pauseplay mode the video pauses for each segment while the caption
        // audio plays — if that audio is muted, every pause is silence for the full duration
        // the narration would have run (the pause is timed by the silently-playing audio, so
        // even with subtitles visible the viewer finishes reading and then sits on a frozen
        // frame). Fires whenever pauseplay + muted caption audio; educational, once per
        // session, like the overlap hint. Controls stay independent — this informs, never acts.
        showDeadAirHint() {
            return this.playbackStyle === 'pauseplay'
                && this.captionVolume === 0
                && !this.dismissedDeadAirHint;
        },
        /* ─── Settings status bar (see template near top of file) ─── */
        // The settings this user EXPECTS: their stored preferences, falling through to
        // CANONICAL_DEFAULTS for anything unset. Mirrors data() initialization exactly —
        // both read from the same constant, so session start can never disagree with baseline.
        baselineSettings() {
            const u = this.$store.state.userData || {};
            return {
                showCaptions: (u.showCaptions !== undefined && u.showCaptions !== null) ? u.showCaptions : CANONICAL_DEFAULTS.showCaptions,
                playbackStyle: (u.playbackStyle !== undefined && u.playbackStyle !== null) ? (u.playbackStyle ? 'continuous' : 'pauseplay') : CANONICAL_DEFAULTS.playbackStyle,
                playbackStyleSameStart: (u.playbackStyleSameStart !== undefined && u.playbackStyleSameStart !== null) ? u.playbackStyleSameStart : CANONICAL_DEFAULTS.playbackStyleSameStart,
                captionVolume: (u.captionAudio !== undefined && u.captionAudio !== null) ? (u.captionAudio ? 1.0 : 0.0) : CANONICAL_DEFAULTS.captionVolume,
                playbackRate: CANONICAL_DEFAULTS.playbackRate,
            };
        },
        // Video volume is COUPLED to caption volume in continuous mode (playerSetVolume(1.0 - captionVolume)
        // in the playbackStyle watcher and adjustSegmentVolume). A video volume of 0 in continuous mode with
        // caption audio at 1.0 is expected behavior, NOT an anomaly. This computed derives what the video
        // volume should be given the current mode, so the anomaly check flags only unexplained deviations
        // (e.g. the user muted via the player bar in pauseplay mode, or a share link set &vv=).
        expectedVideoVolume() {
            return this.playbackStyle === 'continuous' ? Math.max(0, 1.0 - this.captionVolume) : 1.0;
        },
        // State-reflecting icons, extracted from the Advanced Controls template so the status bar
        // and the panel render from one source and cannot drift apart.
        captionsIcon() {
            return this.showCaptions ? 'mdi-closed-caption' : 'mdi-closed-caption-outline';
        },
        playbackStyleIcon() {
            return (this.playbackStyle == 'pauseplay') ? 'mdi-cog-play' : 'mdi-cog-sync';
        },
        captionVolumeIcon() {
            return (this.captionVolume <= 0) ? 'mdi-volume-off' : (this.captionVolume <= 0.3) ? 'mdi-volume-low' : (this.captionVolume <= 0.7) ? 'mdi-volume-medium' : 'mdi-volume-high';
        },
        speedIcon() {
            return this.playbackRate == 1.0 ? 'mdi-play-speed' : this.playbackRate > 1.0 ? 'mdi-speedometer' : 'mdi-speedometer-slow';
        },
        // Every way the current session deviates from baseline. Each entry carries the icon to show,
        // a human-readable label (rendered via the NATIVE title attribute — v-tooltip nested in
        // activator slots breaks Vuetify 2 menus), and which Advanced Control a tap should open.
        settingsAnomalies() {
            const base = this.baselineSettings;
            const list = [];
            if (this.showCaptions !== base.showCaptions) {
                list.push({ key: 'captions', control: 'captions', icon: this.captionsIcon, label: this.showCaptions ? 'Captions: on' : 'Captions: off' });
            }
            if (this.playbackStyle !== base.playbackStyle) {
                list.push({ key: 'playbackStyle', control: 'playbackStyle', icon: this.playbackStyleIcon, label: this.playbackStyle === 'continuous' ? 'Playback style: continuous' : 'Playback style: pause/play' });
            }
            // Overlap mode is a sub-setting of continuous; in pauseplay it is inert and not worth flagging.
            // Carries a component name (custom SVG glyph) instead of an mdi font string — the template
            // renders <component :is> for these and <v-icon> for the rest.
            if (this.playbackStyle === 'continuous' && this.playbackStyleSameStart !== base.playbackStyleSameStart) {
                list.push({ key: 'overlapMode', control: 'playbackStyle', component: this.playbackStyleSameStart ? 'icon-overlap-skip' : 'icon-overlap-chop', icon: null, label: this.playbackStyleSameStart ? 'Overlapping segments: skip' : 'Overlapping segments: chop' });
            }
            if (Math.abs(this.captionVolume - base.captionVolume) > 0.01) {
                list.push({ key: 'captionVolume', control: 'volume', icon: this.captionVolumeIcon, label: 'Caption audio: ' + Math.round(this.captionVolume * 100) + '%' });
            }
            if (Math.abs(this.videoVolume - this.expectedVideoVolume) > 0.01) {
                list.push({ key: 'videoVolume', control: 'volume', icon: this.videoIsMuted ? 'mdi-video-off' : 'mdi-video-minus', label: this.videoIsMuted ? 'Video: muted' : 'Video volume: ' + Math.round(this.videoVolume * 100) + '%' });
            }
            if (this.playbackRate !== base.playbackRate) {
                list.push({ key: 'speed', control: 'speed', icon: this.speedIcon, label: 'Speed: ' + this.playbackRate + 'x' });
            }
            return list;
        },
        // Identity of the current anomaly SET (not values). Dismissing the bar stores this key;
        // if a different setting later deviates (or the overlap hint activates) the key changes
        // and the bar returns on its own.
        settingsAnomaliesKey() {
            return (this.senderSettingsActive ? 'sender|' : '') + (this.showOverlapHint ? 'hint|' : '') + (this.showDeadAirHint ? 'deadair|' : '') + this.settingsAnomalies.map((a) => a.key).join('|');
        },
        showSettingsStatusBar() {
            const hasContent = this.senderSettingsActive || this.settingsAnomalies.length > 0 || this.showOverlapHint || this.showDeadAirHint;
            return hasContent && this.statusBarDismissedKey !== this.settingsAnomaliesKey;
        },
    },
    beforeDestroy: function () {
        this.audioPlayer.pause();
        document.removeEventListener("visibilitychange", this.visibilityChanged);
        clearTimeout(this.postViewedCounter); // if they haven't stayed on the page for 7 seconds the timeout will stay active unless being destroyed
        this.audioPlayer.removeEventListener("ended", this.audioEnded);
        this.audioPlayer.removeEventListener("canplaythrough", this.audioCanPlayThrough);
    },
    created: function () {
        // console.log("postId: ", this.postId);
        this.setPost(1);
    },
    mounted: function () {
        document.addEventListener("visibilitychange", this.visibilityChanged);

        // Pinned post override (existing behavior — do not remove)
        // NOTE (Jul 2026): these values now coincide with CANONICAL_DEFAULTS, so for viewers
        // with no stored preference this is a no-op and the status bar shows nothing. It still
        // matters (and still flags) for viewers whose SAVED settings differ — intentionally.
        if (this.postId === "rXm4nZfBwDP9DIeycLzS") {
            this.captionVolume = 1.0;
            this.playbackStyle = "continuous";
        }

        // Sender settings override — applied when share link includes &share=1
        // These are session-only. They never write to localStorage or userData.
        const q = this.$route.query;
        if (q.share === '1') {
            if (q.ps === 'continuous' || q.ps === 'pauseplay') {
                this.playbackStyle = q.ps;
            }
            if (q.sc !== undefined) {
                this.showCaptions = q.sc === '1';
            }
            if (q.cv !== undefined) {
                const vol = parseFloat(q.cv);
                if (!isNaN(vol) && vol >= 0 && vol <= 1) {
                    this.captionVolume = vol;
                }
            }
            if (q.vv !== undefined) {
                const vol = parseFloat(q.vv);
                if (!isNaN(vol) && vol >= 0 && vol <= 1) {
                    this.videoVolume = vol;
                }
            }
            this.senderSettingsActive = true; // triggers the Option B banner
        }

        this.audioPlayer.volume = this.captionVolume;
    },
  
};
</script>

<style scoped>
@import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

.dialog-bottom-transition-enter-active,
.dialog-bottom-transition-leave-active {
    transition: opacity 0.5s, transform 0.5s;
}
.dialog-bottom-transition-enter {
    opacity: 0;
    transform: translateY(100%);
}
.dialog-bottom-transition-leave-to {
    opacity: 0;
    transform: translateY(100%);
}

.v-input >>> .v-input__prepend-outer {
    margin-right: 0 !important;
}

.v-input >>> .v-input__append-outer {
    margin-left: 0 !important;
}

.expand {
    min-height: 105px !important;
    max-height: 600px !important;
    padding-bottom: 1em;
    /* overflow-y: scroll !important; */
}

.faded {
    position: absolute; 
    bottom: 1em;
    /* width: 100%; */
    width: 95%;
    height: 3em;
    background-image: linear-gradient(to bottom, rgba(255,255,255, 0) 0%, rgba(255,255,255, 1) 75%);
}

.faded-dark {
    position: absolute; 
    bottom: 1em;
    width: 90%;
    height: 3em;
    background-image: linear-gradient(to bottom, rgba(0,0,0, 0) 0%,rgba(18,18,18, 1) 75%);
    transition: display .4s;
}

.bordered {
    background-color: var(--v-background-base);
    /* border: 5px solid var(--v-background-base); */
    border-radius: 25px;
}
.outlined {
    background-color: var(--v-background-base);
    border: 2px solid grey;
}

.v-skeleton-loader__image {
    height: 100%;
}

.search-match-marker {
    background-color: #FF5722 !important; /* fallback; overridden at runtime by marker class per color */
    width: 3px !important;
    opacity: 0.9;
}
</style>